import { ErrorHandler, inject, Injectable } from "@angular/core";

import { API_REQUEST_METHODS, HttpService } from "src/ancestors/base-api.service";
import { ApplicationError } from "src/ancestors/exception.service";
import { TokenStorageService } from "src/app/global-service/token-storage.service";
import { BaseLoginInfo } from "./user.service";
import { Organization, User, OrganizationHistory, UserApproval, UserOptions, UserHistory, FacilityComponentType, UploadTypeEnum, Attachment, TicketStatusEnum, TicketHistory, MaintenanceContract, MaintenanceContractListView, MaintenanceContractStatusEnum, MaintenanceContractDetails, ContractRateCard, RateCardItemTypeEnum, LaborTypeEnum, ActivityTypeEnum, MealTypeEnum, TransportTypeEnum, MaintenanceSectionDetails, MaintenanceSectionRequest, MaintenanceContractFacilityAndFacilityComponent, MaintenanceContractExpenseReportsDetails, ExpenseReportDetails, ExpenseReportItem, TransportRentalTypeEnum, ContractMaintenanceView, FacilityMaintenanceView, OrganizationManagerRequest } from "tht-impianti-cl";
import { Facility, FacilityType, Ticket, FacilityComponent, Area, OrganizationTypeEnum } from "tht-impianti-cl";
import { Checklist, ChecklistDetails, ChecklistListView, ChecklistRequest, ChecklistRevision } from "../models/checklist.model";
// Le compilazioni usano direttamente le classi generate da open-api.json: diversamente dai tipi
// del modello, qui non esiste un doppione locale da cui migrare.
import { ChecklistAnswerRequest, ChecklistSubmissionDetails, ChecklistSubmissionListView, ChecklistSubmissionRequest, ChecklistSubmissionStatusEnum } from "tht-impianti-cl";
import { TenantPublicInfo } from "../models/tenant.model";
import { MaintenanceContractFormRequest } from "src/app/shared/util/maintenance-contract-form-to-request";
import { History, HistoryDiffInterface } from "../models/history.model";
import { TicketCommentExtended } from "../pages/tickets-details/tickets-details.component";

/** Voce rapportino per POST add-items (`expenseItemId` e `totalCost` gestiti dal backend) */
export type ExpenseReportAddItemsEntry = Omit<ExpenseReportItem, "expenseItemId" | "totalCost">;
import { DashboardCounters, TicketFindParams, DashboardTicketsByFacility, DashboardPriorityDistribution, DashboardStatusDistribution, DashboardTicketsLast2Years, FacilityFindParams, FacilityTypeFindParams, FacilityComponentTypeFindParams, MaintenanceContractFindParams, OrganizationFindParams } from "./backend-interfaces";


export interface UserParams {
  email: string;
  password: string;
  langCode?: string;
  deviceType?: string;
  userAgent?: string;
  createPersistentUserAuth?: string;
  recaptcha?: string;
}

export interface UserFilter {
  status?: string;
  roles?: string[];
  organizations?: string[]; // Campo principale per array di organizzazioni
  organization?: string; // Mantiene per compatibilità - deprecato
  organizationIds?: string[]; // Campo principale per array di organization IDs
  organizationId?: string; // Mantiene per compatibilità - deprecato
  username?: string;
  areaId?: string[] | null;
  email?: string;
  cid?: string;
  forename?: string;
  surname?: string;
  userId?: string;
  /**
   * Contesto dell'utente, cioe' il tipo della sua organizzazione.
   *
   * Filtro OPT-IN, sostenuto oggi dal solo `GET /areas/:areaId/users`: serve alla tendina dei
   * referenti, che ammette i soli utenti di contesto Cliente. Il filtro non e' replicabile qui nel
   * frontend, perche' il modello `User` non porta l'organizzazione.
   */
  organizationType?: OrganizationTypeEnum;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

@Injectable()
export class BackendService extends HttpService {
  constructor() {
    super();
    this.logger.setCaller("BackendService");
    this.createInstance({
      url: this.env.apiBaseUrl,
      minimum: 1500
    });
  }

  private tkStorage: TokenStorageService = inject(TokenStorageService);
  private errorHandler = inject(ErrorHandler);

  /** AUTH */

  /**
   * @param loginParam parametri per eseguire il login.
   */
  async localLogin(loginParam: UserParams): Promise<BaseLoginInfo | undefined> {
    try {
      const loginRes = await this.httpClient<BaseLoginInfo>("authn/login", {
        method: API_REQUEST_METHODS.POST,
        body: loginParam
      });
      if (loginRes) {
        return loginRes;
      }
    } catch (error) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Verifica il codice OTP per completare l'autenticazione MFA
   * @param otp Codice OTP a 6 cifre dall'app authenticator
   */
  async verifyMfaOtp(otp: string): Promise<BaseLoginInfo | undefined> {
    try {
      const mfaRes = await this.httpClient<BaseLoginInfo>("authn/verifyMfa", {
        method: API_REQUEST_METHODS.POST,
        body: { otp: otp }
      });
      if (mfaRes) {
        return mfaRes;
      }
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Stato dell'MFA per l'utente corrente, letto dal server.
   *
   * `mfaEnabled` dice se l'utenza ha il secondo fattore attivo, `mfaSessionVerified` se QUESTA
   * sessione l'ha superato (claim del JWT firmato). Prima l'interfaccia ricavava entrambe le cose
   * da un flag in `localStorage` che scriveva da sé: un dato di sicurezza scritto dal client, e
   * quindi modificabile da chi lo usa.
   */
  async getMfaStatus(): Promise<{ mfaEnabled: boolean, mfaSessionVerified: boolean } | undefined> {
    try {
      return await this.httpClient<{ mfaEnabled: boolean, mfaSessionVerified: boolean }>("authn/mfaStatus", {
        method: API_REQUEST_METHODS.GET
      });
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Completa il setup MFA per l'utente corrente
   */
  async completeMfaSetup(): Promise<boolean | undefined> {
    try {
      const mfaRes = await this.httpClient<boolean>("authn/completeMfaSetup", {
        method: API_REQUEST_METHODS.POST
      });
      return mfaRes;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Configura l'MFA per l'utente corrente
   * @returns Promise con i dati di setup MFA (QR code, secret, etc.)
   */
  async setupMfa(): Promise<{ secret: unknown, qrCodeDataUrl: string, manualEntryKey: string } | undefined> {
    try {
      const mfaSetup = await this.httpClient<{ secret: unknown, qrCodeDataUrl: string, manualEntryKey: string }>("authn/setupMfa", {
        method: API_REQUEST_METHODS.POST
      });
      if (mfaSetup) {
        return mfaSetup;
      }
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Disabilita l'MFA per l'utente corrente
   * @param mfaToken Codice OTP per confermare l'identità
   */
  async disableMfa(mfaToken: string): Promise<boolean | undefined> {
    try {
      const result = await this.httpClient<boolean>("authn/disableMfa", {
        method: API_REQUEST_METHODS.POST,
        body: { mfaToken: mfaToken }
      });
      return result;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Recupera l'elenco dei tenant disponibili per l'utente corrente
   * Disponibile solo per admin e super admin
   */
  async getAvailableTenants(): Promise<import("../models/tenant.model").TenantInfo[] | undefined> {
    try {
      const tenants = await this.httpClient<import("../models/tenant.model").TenantInfo[]>("authn/listTenants", {
        method: API_REQUEST_METHODS.GET
      });
      return tenants;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Richiede il cambio tenant per l'utente corrente
   * @param tenantId ID del tenant di destinazione
   */
  async requestTenantSwitch(tenantId: string): Promise<{ token: BaseLoginInfo, sessionId: string } | undefined> {
    try {
      const switchResult = await this.httpClient<{ token: BaseLoginInfo, sessionId: string }>("authn/requestTenantSwitch", {
        method: API_REQUEST_METHODS.POST,
        body: { tenantId: tenantId }
      });
      return switchResult;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Completa il cambio tenant con il token temporaneo
   * @param sessionId Optional sessionId for enhanced CSRF validation
   */
  async completeTenantSwitch(token: string, sessionId: string): Promise<BaseLoginInfo | undefined> {
    try {
      // Prepare enhanced CSRF validation data
      const body: Record<string, string> = {};

      if (sessionId && token) {
        body["sessionId"] = sessionId;
        body["token"] = token;
      } else {
        throw new Error("Session ID and token are required");
      }

      const completeResult = await this.httpClient<BaseLoginInfo>("authn/completeTenantSwitch", {
        method: API_REQUEST_METHODS.POST,
        body: Object.keys(body).length > 0 ? body : undefined
      });
      return completeResult;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
    return;
  }

  /**
   * Gets the CSRF token from browser cookies
   * @private
   */
  private getCsrfTokenFromCookie(): string | null {
    try {
      // Check for CSRF token in cookies - the cookie name depends on configuration
      const cookieName = "THT_FACILITY-csrf-token"; // Use default name for now
      const cookies = document.cookie.split(";");

      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === cookieName) {
          return decodeURIComponent(value);
        }
      }
      return null;
    } catch (error: unknown) {
      this.logger.warn("Failed to get CSRF token from cookie");
      return null;
    }
  }



  /**
   * Rinnova il token di autenticazione ed invalida il precedente.
   */
  async refreshToken(): Promise<BaseLoginInfo | undefined> {
    try {
      const refreshToken = await this.httpClient<BaseLoginInfo>("authn/refreshToken", {
        method: API_REQUEST_METHODS.POST
      });

      if (refreshToken) {
        return refreshToken;
      }

    } catch (error: unknown) {
      this.errorHandler.handleError(error);
    }

    return;
  }


  /**
   * @returns Invalida il token dell'utente e ne impedisce l'uso e il rinnovo.
   */
  public async logout(redirectToLogin?: string): Promise<boolean> {
    try {
      const logoutRes = await this.httpClient<BaseLoginInfo>("authn/logout", {
        method: API_REQUEST_METHODS.POST
      });

      if (logoutRes) {
        return true;
      }
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
    } finally {
      this.tkStorage.deleteToken(this.env.localTokenKey);
      await this.router.navigate([redirectToLogin ? redirectToLogin : this.env.loginUrl]);
    }

    return false;
  }

  // organizzazioni
  async getOrganizations(params?: OrganizationFindParams): Promise<Organization[]> {
    const organizations = await this.httpClient<Organization[]>("organizations", {
      method: API_REQUEST_METHODS.GET,
      query: { ...params }
    });

    return organizations;
  }

  /**
   * Organizzazioni selezionabili nel form del ticket.
   *
   * Non usa `getOrganizations`: quella chiama `GET /organizations`, protetta da
   * `READ_ORGANIZATION_LIST`, che e' il permesso della PAGINA delle organizzazioni. Chi apre o
   * trasferisce un ticket non deve averlo, e infatti Manutentore e Ticket Agent non lo hanno piu':
   * questo endpoint e' autorizzato dall'operazione sul ticket.
   */
  async getTicketAvailableOrganizations(fromRecord?: number, numRecords?: number, name?: string): Promise<Organization[]> {
    return this.httpClient<Organization[]>("tickets/available-organizations", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name
      }
    });
  }

  /**
   * Impianti selezionabili nel form del ticket.
   *
   * Restituisce anche `organizationIds`, da cui il form deriva l'organizzazione preselezionata
   * quando si sceglie l'impianto.
   */
  async getTicketAvailableFacilities(fromRecord?: number, numRecords?: number, name?: string): Promise<Facility[]> {
    return this.httpClient<Facility[]>("tickets/available-facilities", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name
      }
    });
  }

  /** Organizzazioni selezionabili nel form dell'impianto e nel filtro della lista impianti */
  async getFacilityAvailableOrganizations(fromRecord?: number, numRecords?: number, name?: string): Promise<Organization[]> {
    return this.httpClient<Organization[]>("facilities/available-organizations", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name
      }
    });
  }

  /**
   * Utenti selezionabili come responsabile dell'impianto.
   *
   * Non usa `getUsers`, protetta da `READ_USER_LIST`: quello e' il permesso della PAGINA degli
   * utenti, con i suoi filtri per ruolo e stato. Qui basta l'identita' che il select mostra.
   */
  async getFacilityAvailableManagers(fromRecord?: number, numRecords?: number, username?: string): Promise<User[]> {
    return this.httpClient<User[]>("facilities/available-managers", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        username
      }
    });
  }

  /** Organizzazioni selezionabili nel form dell'utente e nel filtro della lista utenti */
  async getUserAvailableOrganizations(fromRecord?: number, numRecords?: number, name?: string): Promise<Organization[]> {
    return this.httpClient<Organization[]>("users/available-organizations", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name
      }
    });
  }

  /** Utenti selezionabili come responsabile dell'organizzazione */
  async getOrganizationAvailableManagers(fromRecord?: number, numRecords?: number, username?: string): Promise<User[]> {
    return this.httpClient<User[]>("organizations/available-managers", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        username
      }
    });
  }

  // organizzazioni
  async getOrganizationsOnTicketPage(fromRecord?: number, numRecords?: number, name?: string): Promise<{ data: Organization[], total: number }> {
    const organizations = await this.httpClient<{ data: Organization[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        searchedText: name,
        fromRecord,
        numRecords,
        field: "organizations"
      }
    });
    return organizations;
  }

  /**
   * Crea un'organizzazione. `newManager` e' la scorciatoia del form: al posto di un `managerId`
   * esistente porta i dati di una nuova utenza, che il backend crea nella stessa transazione. I due
   * campi sono alternativi — inviarli entrambi e' un 400.
   */
  async createOrganization(organizationData: Omit<Organization, "organizationId"> & { newManager?: OrganizationManagerRequest }): Promise<Organization> {
    const organization = await this.httpClient<Organization>("organizations", {
      method: API_REQUEST_METHODS.POST,
      body: organizationData
    });

    return organization;
  }

  /**
   * Aggiorna un'organizzazione. Come nella creazione, `newManager` crea l'utenza e la imposta come
   * responsabile in alternativa a un `managerId` esistente; richiede UPDATE_ORGANIZATION, non basta
   * il permesso sui soli contatti.
   */
  async updateOrganization(organizationId: string, organizationData: Partial<Organization> & { newManager?: OrganizationManagerRequest }): Promise<Organization> {
    const organization = await this.httpClient<Organization>(`organizations/${organizationId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: organizationData
    });

    return organization;
  }

  async deleteOrganization(organizationId: string): Promise<void> {
    await this.httpClient(`organizations/${organizationId}`, {
      method: API_REQUEST_METHODS.DELETE
    });
  }

  // impianti
  async getFacilities(params?: FacilityFindParams, sort?: { column: string, direction: "asc" | "desc" }): Promise<Facility[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    const facilities = await this.httpClient<Facility[]>("facilities", {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams,
        sortBy: sort?.column,
        sortOrder: sort?.direction
      }
    });

    return facilities;
  }

  async createFacility(facilityData: Omit<Facility, "facilityId">): Promise<Facility> {
    const facility = await this.httpClient<Facility>("facilities", {
      method: API_REQUEST_METHODS.POST,
      body: facilityData
    });

    return facility;
  }

  async updateFacility(facilityId: string, facilityData: Partial<Facility>): Promise<Facility> {
    const facility = await this.httpClient<Facility>(`facilities/${facilityId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: facilityData
    });

    if (!facility) {
      throw new Error("Failed to update facility");
    }

    return facility;
  }

  /**
   * Recupera i tipi di impianto, con filtri e paginazione opzionali.
   *
   * Senza argomenti restituisce l'elenco completo: è così che la usa il form dell'impianto per
   * popolare la select, e nessun default di paginazione deve troncarlo.
   * @param params - Filtri e paginazione opzionali
   * @returns Promise con i tipi di impianto che corrispondono ai filtri
   */
  async getFacilityTypes(params?: FacilityTypeFindParams): Promise<FacilityType[]> {
    const facilityTypes = await this.httpClient<FacilityType[]>("facility-types", {
      method: API_REQUEST_METHODS.GET,
      query: params
    });

    return facilityTypes;
  }

  /**
   * Conta i tipi di impianto che corrispondono ai filtri.
   * @param params - Filtri opzionali su nome e categoria
   * @returns Promise con il numero di tipi di impianto
   */
  async getFacilityTypesCount(params?: Omit<FacilityTypeFindParams, "fromRecord" | "numRecords">): Promise<{ count: number }> {
    const result = await this.httpClient<{ count: number }>("facility-types/count", {
      method: API_REQUEST_METHODS.GET,
      query: params
    });

    return result;
  }

  /**
   * Crea un nuovo tipo di impianto.
   * @param facilityTypeData - Dati del tipo di impianto da creare
   * @returns Promise con il tipo di impianto creato
   * @throws {Error} Se il backend non restituisce il tipo creato
   */
  async createFacilityType(facilityTypeData: Omit<FacilityType, "facilityTypeId">): Promise<FacilityType> {
    const facilityType = await this.httpClient<FacilityType>("facility-types", {
      method: API_REQUEST_METHODS.POST,
      body: facilityTypeData
    });
    if (!facilityType) {
      throw new Error("Failed to create facility type");
    }

    return facilityType;
  }

  /**
   * Aggiorna un tipo di impianto esistente.
   * @param facilityTypeId - ID del tipo di impianto da aggiornare
   * @param facilityTypeData - Campi da aggiornare
   * @returns Promise con il tipo di impianto aggiornato
   * @throws {Error} Se l'ID è vuoto o il backend non restituisce il tipo aggiornato
   */
  async updateFacilityType(facilityTypeId: string, facilityTypeData: Partial<FacilityType>): Promise<FacilityType> {
    if (!facilityTypeId?.trim()) {
      throw new Error("ID tipo di impianto è richiesto");
    }
    const id = encodeURIComponent(facilityTypeId.trim());
    const facilityType = await this.httpClient<FacilityType>(`facility-types/${id}`, {
      method: API_REQUEST_METHODS.PUT,
      body: facilityTypeData
    });
    if (!facilityType) {
      throw new Error("Failed to update facility type");
    }

    return facilityType;
  }

  /**
   * Elimina un tipo di impianto.
   *
   * Il backend rifiuta l'eliminazione con `FACILITY_TYPE_HAS_FACILITIES` se esiste almeno un
   * impianto che usa questo tipo.
   * @param facilityTypeId - ID del tipo di impianto da eliminare
   * @returns Promise con l'esito dell'eliminazione
   * @throws {Error} Se l'ID è vuoto
   */
  async deleteFacilityType(facilityTypeId: string): Promise<boolean> {
    if (!facilityTypeId?.trim()) {
      throw new Error("ID tipo di impianto è richiesto");
    }
    const id = encodeURIComponent(facilityTypeId.trim());
    const response = await this.httpClient<boolean>(`facility-types/${id}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response ?? false;
  }

  async findGeocode(address: string): Promise<GeocodeResult[]> {
    const geocodeResult = await this.httpClient<GeocodeResult[]>("facilities/geocode/fetch", {
      method: API_REQUEST_METHODS.GET,
      query: {
        address
      }
    });

    return geocodeResult;
  }

  // aree
  async getAreas(fromRecord?: number, numRecords?: number, name?: string, withIcons?: boolean): Promise<Area[]> {
    const areas = await this.httpClient<Area[]>("areas/myareas", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name,
        withIcons
      }
    });

    return areas;
  }

  async getAllAreas(fromRecord?: number, numRecords?: number, name?: string): Promise<Area[]> {
    const areas = await this.httpClient<Area[]>("areas", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        name
      }
    });

    return areas;
  }

  async getArea(areaId: string): Promise<Area & { users?: User[] } | null> {
    const area = await this.httpClient<Area & { users?: User[] }>(`areas/${areaId}/details`, {
      method: API_REQUEST_METHODS.GET
    });

    return area;
  }

  async createArea(areaData: Omit<Area, "areaId"> & { referentIds?: string[] }): Promise<Area> {
    // deve essere in multipart/form-data
    const area = await this.httpClient<Area>("areas", {
      method: API_REQUEST_METHODS.POST,
      body: areaData
    });
    if (!area) {
      throw new Error("Failed to create area");
    }

    return area;
  }

  async updateArea(areaId: string, areaData: Partial<Area> & { referentIds?: string[] }): Promise<Area> {
    const area = await this.httpClient<Area>(`areas/${areaId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: areaData
    });
    if (!area) {
      throw new Error("Failed to update area");
    }

    return area;
  }

  async deleteArea(areaId: string): Promise<Area> {
    const area = await this.httpClient<Area>(`areas/${areaId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return area;
  }

  async getAreasCount(name?: string): Promise<{ count: number }> {
    const result = await this.httpClient<{ count: number }>("areas/count", {
      method: API_REQUEST_METHODS.GET,
      query: {
        name
      }
    });

    return result;
  }

  async getUsersCountInArea(areaId: string): Promise<number> {
    const result = await this.httpClient<{ count: number }>(`areas/${areaId}/users/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return result.count;
  }

  async getUsersInArea(areaId: string, fromRecord?: number, numRecords?: number, filter?: UserFilter): Promise<User[]> {
    const users = await this.httpClient<User[]>(`areas/${areaId}/users`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords,
        ...filter
      }
    });
    return users;
  }

  /**
   * Referenti di un'area. Speculare a `getOrganizationManager`, ma plurale: un'area può avere
   * più referenti. Lista vuota se non ne ha.
   * @param areaId - ID dell'area
   */
  async getAreaReferents(areaId: string): Promise<User[]> {
    const referents = await this.httpClient<User[]>(`areas/${areaId}/referents`, {
      method: API_REQUEST_METHODS.GET
    });
    return referents ?? [];
  }

  /**
   * Aree di cui l'utente corrente è referente. È il predicato che apre la pagina delle checklist
   * condivise: non dipende dal ruolo ma dall'essere stati impostati come referenti.
   */
  async getAreasReferredByMe(): Promise<Area[]> {
    const areas = await this.httpClient<Area[]>("areas/referred-by-me", {
      method: API_REQUEST_METHODS.GET
    });
    return areas ?? [];
  }

  // tickets
  async getTickets(params?: TicketFindParams, sort?: { column: string, direction: "asc" | "desc" }) {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    if (params?.title) {
      queryParams.title = params.title;
    }

    const tickets = await this.httpClient<Ticket[]>("tickets", {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams,
        sortBy: sort?.column,
        sortOrder: sort?.direction
      }
    });
    return tickets;
  }

  async searchTickets(searchValue: string, params?: TicketFindParams, sort?: { column: string, direction: "asc" | "desc" }): Promise<{ tickets: Ticket[], totalCount: number }> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }

    const tickets = await this.httpClient<{ tickets: Ticket[], totalCount: number }>("tickets/search", {
      method: API_REQUEST_METHODS.GET,
      query: {
        searchTerm: searchValue,
        ...queryParams,
        sortBy: sort?.column,
        sortOrder: sort?.direction
      }
    });
    return { tickets: tickets.tickets, totalCount: tickets.totalCount };
  }

  async getTicketsCount(params?: TicketFindParams): Promise<number> {
    const queryParams = { ...params };
    const ticketsCount = await this.httpClient<{ count: number }>("tickets/count", {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams
      }
    });
    return ticketsCount.count;
  }

  async excelExportTickets(params?: TicketFindParams): Promise<{ fileName: string, fileUrl: string }> {
    const queryParams = { ...params };
    const tickets = await this.httpClient<{ fileName: string, fileUrl: string }>("tickets/excel-export", {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams
      },
      isFileDownload: true
    });
    return tickets;
  }

  /**
   * Crea un ticket con allegati usando multipart/form-data
   */
  async createTicketWithAttachments(formData: FormData): Promise<Ticket> {
    const ticket = await this.httpClient<Ticket>("tickets", {
      method: API_REQUEST_METHODS.POST,
      body: formData,
      formData: true
    });

    return ticket;
  }

  /**
   * Get tenant public information based on the referer header
   */
  async getTenantPublicInfo(): Promise<TenantPublicInfo | undefined> {
    try {
      const tenantInfo = await this.httpClient<TenantPublicInfo>("tenants/info/public", {
        method: API_REQUEST_METHODS.GET
      });

      if (tenantInfo) {
        return tenantInfo;
      }

    } catch (error: unknown) {
      this.errorHandler.handleError(error);
    }

    return;
  }

  // organizzazioni
  async getOrganization(organizationId: string): Promise<Organization | null> {
    const organization = await this.httpClient<Organization>(`organizations/${organizationId}/details`, {
      method: API_REQUEST_METHODS.GET
    });

    return organization;
  }

  async getOrganizationHistory(organizationId: string): Promise<OrganizationHistory[] | null> {
    const organizationHistory = await this.httpClient<OrganizationHistory[]>(`organizations/${organizationId}/details/history`, {
      method: API_REQUEST_METHODS.GET
    });

    return organizationHistory;
  }

  async getFacilitiesByOrganization(organizationId: string, params?: { fromRecord?: number, numRecords?: number }): Promise<Facility[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    const facilities = await this.httpClient<Facility[]>(`organizations/${organizationId}/facilities`, {
      method: API_REQUEST_METHODS.GET,
      query: queryParams
    });
    return facilities;
  }

  async getFacilitiesCountByOrganization(organizationId: string): Promise<number> {
    const res = await this.httpClient<{ count: number }>(`organizations/${organizationId}/facilities/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return res.count;
  }

  async getOrganizationsCount(params?: Omit<OrganizationFindParams, "fromRecord" | "numRecords">): Promise<number> {
    // gli stessi filtri dell'elenco, o il totale di paginazione non corrisponde alle righe
    const res = await this.httpClient<{ count: number }>("organizations/count", {
      method: API_REQUEST_METHODS.GET,
      query: { ...params }
    });

    return res.count;
  }

  async getFacilitiesCount(areaId: string | null, filters?: FacilityFindParams): Promise<number> {
    const res = await this.httpClient<{ count: number }>("facilities/count", {
      method: API_REQUEST_METHODS.GET,
      query: {
        areaIds: areaId ? [areaId] : undefined,
        ...filters
      }
    });

    return res.count;
  }

  // users
  async getUsers(params?: { fromRecord?: number, numRecords?: number, filter?: UserFilter }, sort?: { column: string, direction: "asc" | "desc" }): Promise<(User & { organization?: Organization })[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    const users = await this.httpClient<User[]>("users", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord: queryParams.fromRecord ?? 0,
        numRecords: queryParams.numRecords ?? 10,
        username: queryParams.filter?.username ?? undefined,
        areaIds: queryParams.filter?.areaId ?? undefined,
        organizationId: queryParams.filter?.organization ?? queryParams.filter?.organizationId ?? undefined,
        status: queryParams.filter?.status ?? undefined,
        roles: queryParams.filter?.roles ?? undefined,
        email: queryParams.filter?.email ?? undefined,
        userId: queryParams.filter?.userId ?? undefined,
        cid: queryParams.filter?.cid ?? undefined,
        sortBy: sort?.column,
        sortOrder: sort?.direction
      }
    });

    return users;
  }

  async createUser(userData: Omit<User, "userId">): Promise<User> {
    const user = await this.httpClient<User>("users", {
      method: API_REQUEST_METHODS.POST,
      body: userData
    });

    if (!user) {
      throw new Error("Error while creating user");
    }

    return user;
  }

  async getUsersCount(areaId: string | null, filter?: UserFilter): Promise<number> {
    const res = await this.httpClient<{ count: number }>("users/count", {
      method: API_REQUEST_METHODS.GET,
      query: {
        areaIds: areaId ? [areaId] : undefined,
        ...filter
      }
    });

    return res.count;
  }

  async getOrganizationUsers(organizationId: string, params?: { fromRecord?: number, numRecords?: number }): Promise<User[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    const users = await this.httpClient<User[]>(`organizations/${organizationId}/users`, {
      method: API_REQUEST_METHODS.GET,
      query: queryParams
    });

    return users;
  }

  async getOrganizationUsersCount(organizationId: string): Promise<number> {
    const res = await this.httpClient<{ count: number }>(`organizations/${organizationId}/users/count`, {
      method: API_REQUEST_METHODS.GET
    });

    return res.count;
  }

  // facilities details
  async getFacility(facilityId: string): Promise<Facility | null> {
    const facility = await this.httpClient<Facility>(`facilities/${facilityId}/details`, {
      method: API_REQUEST_METHODS.GET
    });

    return facility;
  }

  /**
   * Conteggio contratti di manutenzione collegati all'impianto (come `organizations/.../facilities/count`).
   * @param facilityId - Identificativo impianto
   * @returns Numero di contratti
   * @throws {Error} Se `facilityId` è assente o vuoto
   */
  async getFacilityMaintenanceContractsCount(facilityId: string): Promise<number> {
    if (!facilityId?.trim()) {
      throw new Error("ID impianto richiesto");
    }
    const res = await this.httpClient<{ count: number }>(`facilities/${facilityId}/contracts/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return res.count;
  }

  /**
   * Elenco paginato contratti di manutenzione collegati all'impianto (richiede scope READ_MAINTENANCE_CONTRACT).
   * @param facilityId - Identificativo impianto
   * @param params - Offset e dimensione pagina (`fromRecord`, `numRecords`), come per gli impianti in organizzazione
   * @returns Lista in formato {@link MaintenanceContractListView}
   * @throws {Error} Se `facilityId` è assente o vuoto
   */
  async getFacilityMaintenanceContractsList(
    facilityId: string,
    params?: { fromRecord?: number; numRecords?: number }
  ): Promise<MaintenanceContractListView[]> {
    if (!facilityId?.trim()) {
      throw new Error("ID impianto richiesto");
    }
    const list = await this.httpClient<MaintenanceContractListView[]>(`facilities/${facilityId}/contracts/list`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord: params?.fromRecord,
        numRecords: params?.numRecords
      }
    });
    return list;
  }

  async deleteFacility(facilityId: string): Promise<Facility> {
    const facility = await this.httpClient<Facility>(`facilities/${facilityId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return facility;
  }

  /**
   * Imposta l'impianto in stato di manutenzione attiva.
   */
  async activateFacilityMaintenance(facilityId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`facilities/${facilityId}/maintenance/start`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  /**
   * True se l'errore indica assenza di finestra manutenzione attiva (404), caso atteso senza banner.
   * @param error - Errore propagato da {@link HttpService.httpClient}
   */
  private isMaintenanceActiveNotFoundError(error: unknown): boolean {
    if (error instanceof ApplicationError) {
      if (error.httpErrorCode === 404) {
        return true;
      }
      if (error.errorKey === "404") {
        return true;
      }
    }
    return false;
  }

  /**
   * Stato finestra manutenzione attiva per l'impianto (GET).
   * 404 → nessuna finestra attiva (`undefined`); altri errori vengono loggati e rilanciati.
   */
  async getFacilityMaintenanceActive(facilityId: string): Promise<FacilityMaintenanceView | undefined> {
    try {
      return await this.httpClient<FacilityMaintenanceView>(`facilities/${facilityId}/maintenance/active`, {
        method: API_REQUEST_METHODS.GET
      });
    } catch (error: unknown) {
      if (this.isMaintenanceActiveNotFoundError(error)) {
        return undefined;
      }
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Termina la manutenzione attiva sull'impianto.
   */
  async stopFacilityMaintenance(facilityId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`facilities/${facilityId}/maintenance/stop`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  /**
   * Estende la manutenzione attiva sull'impianto (backend applica estensione predefinita).
   */
  async extendFacilityMaintenance(facilityId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`facilities/${facilityId}/maintenance/extend`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  async getFacilityHistory(facilityId: string): Promise<History[]> {
    const facilityHistory = await this.httpClient<History[]>(`facilities/${facilityId}/history`, {
      method: API_REQUEST_METHODS.GET
    });

    return facilityHistory;
  }

  async getFacilityStatusHistory(facilityId: string): Promise<History[]> {
    const facilityStatusHistory = await this.httpClient<History[]>(`facilities/${facilityId}/status-history`, {
      method: API_REQUEST_METHODS.GET
    });

    return facilityStatusHistory;
  }
  async getTicketsByFacility(facilityId: string): Promise<Ticket[]> {
    const tickets = await this.httpClient<Ticket[]>(`tickets/facility/${facilityId}`, {
      method: API_REQUEST_METHODS.GET
    });

    return tickets;
  }

  async getTicket(ticketId: string): Promise<Ticket | null> {
    try {
      const ticket = await this.httpClient<Ticket>(`tickets/${ticketId}/details`, {
        method: API_REQUEST_METHODS.GET
      });
      return ticket;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      return null;
    }
  }

  // user details
  async getUser(userId: string, includeOptions: boolean = false): Promise<(User & { organizations?: Organization[] }) | null> {
    const user = await this.httpClient<(User & { organizations?: Organization[] })>(`users/${userId}/details`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        includeOptions
      }
    });

    return user;
  }

  async getOrganizationManager(organizationId: string): Promise<User | null> {
    const manager = await this.httpClient<User>(`organizations/${organizationId}/manager`, {
      method: API_REQUEST_METHODS.GET
    });
    return manager;
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    const user = await this.httpClient<User>(`users/${userId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: userData
    });

    return user;
  }

  async updateUserOptions(userId: string, userOptions: Partial<UserOptions>): Promise<boolean> {
    const res = await this.httpClient<boolean>(`users/${userId}/options`, {
      method: API_REQUEST_METHODS.PUT,
      body: userOptions
    });

    return res;
  }

  async getUserOrganizations(userId: string): Promise<Organization[]> {
    const organizations = await this.httpClient<Organization[]>(`users/${userId}/organizations`, {
      method: API_REQUEST_METHODS.GET
    });
    return organizations;
  }

  async getUserOrganizationsCount(userId: string): Promise<number> {
    const count = await this.httpClient<{ count: number }>(`users/${userId}/organizations/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return count.count;
  }

  // facility components
  async getFacilityComponents(fromRecord?: number, numRecords?: number): Promise<FacilityComponent[]> {
    const components = await this.httpClient<FacilityComponent[]>("facility-components", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords
      }
    });

    return components;
  }

  async createFacilityComponent(componentData: Omit<FacilityComponent, "facilityComponentId">): Promise<FacilityComponent> {
    const component = await this.httpClient<FacilityComponent>("facility-components", {
      method: API_REQUEST_METHODS.POST,
      body: componentData
    });

    return component;
  }

  async updateFacilityComponent(componentId: string, componentData: Partial<FacilityComponent>): Promise<FacilityComponent> {
    const component = await this.httpClient<FacilityComponent>(`facility-components/${componentId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: componentData
    });

    return component;
  }

  async getFacilityComponentsCount(facilityId: string): Promise<number> {
    const result = await this.httpClient<{ count: number }>(`facility-components/facility/${facilityId}/count`, {
      method: API_REQUEST_METHODS.GET
    });

    return result.count;
  }

  async getComponentsCount(): Promise<number> {
    const result = await this.httpClient<{ count: number }>("facility-components/count", {
      method: API_REQUEST_METHODS.GET
    });

    return result.count;
  }

  async getFacilityComponent(componentId: string): Promise<FacilityComponent | null> {
    const component = await this.httpClient<FacilityComponent>(`facility-components/${componentId}/details`, {
      method: API_REQUEST_METHODS.GET
    });

    return component;
  }

  async getFacilityComponentHistory(componentId: string): Promise<History[] | null> {
    const history = await this.httpClient<History[]>(`facility-components/${componentId}/history`, {
      method: API_REQUEST_METHODS.GET
    });

    return history;
  }

  async getUserHistory(userId: string): Promise<History[] | null> {
    const userHistory = await this.httpClient<History[]>(`users/${userId}/history`, {
      method: API_REQUEST_METHODS.GET
    });

    return userHistory;
  }

  // user approvals
  async getUserApprovals(params?: { fromRecord?: number, numRecords?: number }): Promise<UserApproval[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }
    const userApprovals = await this.httpClient<UserApproval[]>("user-approvals", {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord: queryParams.fromRecord,
        numRecords: queryParams.numRecords
      }
    });

    return userApprovals;
  }

  async getUserApprovalsCount(): Promise<number> {
    const res = await this.httpClient<{ count: number }>("user-approvals/count", {
      method: API_REQUEST_METHODS.GET
    });

    return res.count;
  }

  async approveUser(userApprovalId: string, status: string, role: string): Promise<User> {
    const user = await this.httpClient<User>(`user-approvals/${userApprovalId}/approve`, {
      method: API_REQUEST_METHODS.PUT,
      body: { status, role }
    });

    return user;
  }

  async getFacilityComponentsByFacility(facilityId: string, fromRecord?: number, numRecords?: number): Promise<FacilityComponent[]> {
    const components = await this.httpClient<FacilityComponent[]>(`facility-components/facility/${facilityId}`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords
      }
    });

    return components;
  }

  async deleteFacilityComponent(componentId: string): Promise<FacilityComponent> {
    const component = await this.httpClient<FacilityComponent>(`facility-components/${componentId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return component;
  }

  async changePasswordRequest(email: string, recaptcha: string): Promise<void> {
    await this.httpClient<string>("authn/changePasswordRequest", {
      method: API_REQUEST_METHODS.POST,
      body: { email, recaptcha }
    });
  }

  async changePassword(tokenId: string, newPassword: string): Promise<void> {
    await this.httpClient<boolean>(`authn/changePassword/${tokenId}`, {
      method: API_REQUEST_METHODS.POST,
      body: { newPassword }
    });
  }

  async getAccountDetails(): Promise<User> {
    const accountDetails = await this.httpClient<User>("account/details", {
      method: API_REQUEST_METHODS.GET
    });

    return accountDetails;
  }

  async getAccountHistory(): Promise<UserHistory[]> {
    const accountHistory = await this.httpClient<UserHistory[]>("account/history", {
      method: API_REQUEST_METHODS.GET
    });

    return accountHistory;
  }

  async updateAccountOptions(userOptions: Partial<UserOptions>): Promise<User> {
    const user = await this.httpClient<User>("account/options", {
      method: API_REQUEST_METHODS.PUT,
      body: {
        langCode: userOptions.langCode,
        timezone: userOptions.timezone,
        avatarImage: userOptions.avatarImage,
        selectedNotifications: userOptions.selectedNotifications,
        selectedEvents: userOptions.selectedEvents
      }
    });

    return user;
  }

  async updateAccountDetails(userData: Partial<User>): Promise<User> {
    const user = await this.httpClient<User>("account", {
      method: API_REQUEST_METHODS.PUT,
      body: userData
    });

    return user;
  }

  async deleteUser(userId: string): Promise<boolean> {
    const res = await this.httpClient<boolean>(`users/${userId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return res;
  }

  // facility component types

  /**
   * Recupera i tipi di componente, con filtri e paginazione opzionali.
   *
   * Senza argomenti restituisce l'elenco completo: è così che la usa il form del componente per
   * popolare la select, e nessun default di paginazione deve troncarlo.
   * @param params - Filtri e paginazione opzionali
   * @returns Promise con i tipi di componente che corrispondono ai filtri
   */
  async getFacilityComponentTypes(params?: FacilityComponentTypeFindParams): Promise<FacilityComponentType[]> {
    const facilityComponentTypes = await this.httpClient<FacilityComponentType[]>("facility-component-types", {
      method: API_REQUEST_METHODS.GET,
      query: params
    });

    return facilityComponentTypes;
  }

  /**
   * Conta i tipi di componente che corrispondono ai filtri.
   * @param params - Filtri opzionali su nome e categoria
   * @returns Promise con il numero di tipi di componente
   */
  async getFacilityComponentTypesCount(params?: Omit<FacilityComponentTypeFindParams, "fromRecord" | "numRecords">): Promise<{ count: number }> {
    const result = await this.httpClient<{ count: number }>("facility-component-types/count", {
      method: API_REQUEST_METHODS.GET,
      query: params
    });

    return result;
  }

  /**
   * Crea un nuovo tipo di componente.
   * @param facilityComponentTypeData - Dati del tipo di componente da creare
   * @returns Promise con il tipo di componente creato
   * @throws {Error} Se il backend non restituisce il tipo creato
   */
  async createFacilityComponentType(facilityComponentTypeData: Omit<FacilityComponentType, "facilityComponentTypeId">): Promise<FacilityComponentType> {
    const facilityComponentType = await this.httpClient<FacilityComponentType>("facility-component-types", {
      method: API_REQUEST_METHODS.POST,
      body: facilityComponentTypeData
    });
    if (!facilityComponentType) {
      throw new Error("Failed to create facility component type");
    }

    return facilityComponentType;
  }

  /**
   * Aggiorna un tipo di componente esistente.
   * @param facilityComponentTypeId - ID del tipo di componente da aggiornare
   * @param facilityComponentTypeData - Campi da aggiornare
   * @returns Promise con il tipo di componente aggiornato
   * @throws {Error} Se l'ID è vuoto o il backend non restituisce il tipo aggiornato
   */
  async updateFacilityComponentType(facilityComponentTypeId: string, facilityComponentTypeData: Partial<FacilityComponentType>): Promise<FacilityComponentType> {
    if (!facilityComponentTypeId?.trim()) {
      throw new Error("ID tipo di componente è richiesto");
    }
    const id = encodeURIComponent(facilityComponentTypeId.trim());
    const facilityComponentType = await this.httpClient<FacilityComponentType>(`facility-component-types/${id}`, {
      method: API_REQUEST_METHODS.PUT,
      body: facilityComponentTypeData
    });
    if (!facilityComponentType) {
      throw new Error("Failed to update facility component type");
    }

    return facilityComponentType;
  }

  /**
   * Elimina un tipo di componente.
   *
   * Il backend rifiuta l'eliminazione con `FACILITY_COMPONENT_TYPE_HAS_COMPONENTS` se esiste almeno
   * un componente che usa questo tipo.
   * @param facilityComponentTypeId - ID del tipo di componente da eliminare
   * @returns Promise con l'esito dell'eliminazione
   * @throws {Error} Se l'ID è vuoto
   */
  async deleteFacilityComponentType(facilityComponentTypeId: string): Promise<boolean> {
    if (!facilityComponentTypeId?.trim()) {
      throw new Error("ID tipo di componente è richiesto");
    }
    const id = encodeURIComponent(facilityComponentTypeId.trim());
    const response = await this.httpClient<boolean>(`facility-component-types/${id}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response ?? false;
  }

  async getTicketsCodes(searchedText?: string, fromRecord?: number, numRecords?: number): Promise<{ data: string[], total: number }> {
    const codes = await this.httpClient<{ data: string[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "code",
        searchedText,
        fromRecord,
        numRecords
      }
    });
    return codes;
  }

  async getTicketsNames(searchedText?: string, fromRecord?: number, numRecords?: number): Promise<{ data: string[], total: number }> {
    const names = await this.httpClient<{ data: string[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "name",
        searchedText,
        fromRecord,
        numRecords
      }
    });

    return names;
  }

  async getTicketsCreationUsers(searchedText?: string, fromRecord?: number, numRecords?: number): Promise<{ data: User[], total: number }> {
    const userIds = await this.httpClient<{ data: User[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "createdBy",
        searchedText,
        fromRecord,
        numRecords
      }
    });

    return userIds;
  }

  async getTicketsAssignedUsers(searchedText?: string, fromRecord?: number, numRecords?: number): Promise<{ data: User[], total: number }> {
    const userIds = await this.httpClient<{ data: User[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "assignedUser",
        searchedText,
        fromRecord,
        numRecords
      }
    });

    return userIds;
  }

  async getFacilitiesOnTicketPage(fromRecord?: number, numRecords?: number, name?: string): Promise<{ data: Facility[], total: number }> {
    const facilities = await this.httpClient<{ data: Facility[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "facility",
        searchedText: name,
        fromRecord,
        numRecords
      }
    });

    return facilities;
  }

  async getMaintenanceContractsOnTicketPage(fromRecord?: number, numRecords?: number, name?: string): Promise<{ data: { maintenanceContractId: string, name: string }[], total: number }> {

    const maintenanceContracts = await this.httpClient<{ data: { maintenanceContractId: string, name: string }[], total: number }>("tickets/search-filters", {
      method: API_REQUEST_METHODS.GET,
      query: {
        field: "maintenanceContract",
        searchedText: name,
        fromRecord,
        numRecords
      }
    });

    return maintenanceContracts;
  }

  async getTicketComments(ticketId: string): Promise<TicketCommentExtended[]> {
    const comments = await this.httpClient<TicketCommentExtended[]>(`tickets/${ticketId}/comments`, {
      method: API_REQUEST_METHODS.GET
    });
    return comments;
  }

  async sendComment(ticketId: string, content: string, isPrivate: boolean, status?: TicketStatusEnum) {
    const body: { content: string, isPrivate: boolean, status?: TicketStatusEnum } = {
      content: content,
      isPrivate: isPrivate
    };
    if (status) {
      body.status = status;
    }
    const response = await this.httpClient<Attachment>(`tickets/${ticketId}/comments`, {
      method: API_REQUEST_METHODS.POST,
      body: body
    });

    return response;
  }

  async deleteComment(commentId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/comments/${commentId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response;
  }

  /**
   * Scarica i byte di un'immagine inline citata da un commento.
   *
   * Il contenuto del commento non trasporta piu' le immagini: porta i soli riferimenti, e i
   * byte si chiedono qui una immagine per volta. Passa da `httpClient` e non da un `<img src>`
   * nativo perche' l'autenticazione e' a cookie con token CSRF in header, che solo le nostre
   * chiamate mettono.
   * @param commentId ID del commento che cita l'immagine
   * @param mediaId ID del media da scaricare
   * @returns Nome del file e object URL del blob scaricato
   */
  async getCommentInlineImage(commentId: string, mediaId: string): Promise<{ fileName: string, fileUrl: string }> {
    const response = await this.httpClient<{ fileName: string, fileUrl: string }>(`tickets/comments/${commentId}/images/${mediaId}`, {
      method: API_REQUEST_METHODS.GET,
      isFileDownload: true
    });

    return response;
  }

  /**
   * Scarica i byte di un'immagine inline citata dalla descrizione di un ticket.
   * @param ticketId ID del ticket la cui descrizione cita l'immagine
   * @param mediaId ID del media da scaricare
   * @returns Nome del file e object URL del blob scaricato
   */
  async getTicketInlineImage(ticketId: string, mediaId: string): Promise<{ fileName: string, fileUrl: string }> {
    const response = await this.httpClient<{ fileName: string, fileUrl: string }>(`tickets/${ticketId}/images/${mediaId}`, {
      method: API_REQUEST_METHODS.GET,
      isFileDownload: true
    });

    return response;
  }

  async editComment(commentId: string, content: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/comments/${commentId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: { content: content }
    });

    return response;
  }

  /**
   * Aggiorna un ticket. `maintenanceContractId: null` stacca il contratto; assente lo lascia invariato.
   */
  async updateTicket(ticketId: string, ticket: Omit<Ticket, "ticketId" | "status" | "maintenanceContractId"> & { attachments: File[]; maintenanceContractId?: string | null }): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}`, {
      method: API_REQUEST_METHODS.PUT,
      body: ticket
    });

    return response;
  }

  async deleteTicket(ticketId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response;
  }

  /**
   * Mette il ticket in manutenzione (endpoint maintenance/active).
   */
  async activateTicketMaintenance(ticketId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}/maintenance/start`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  /**
   * Termina la manutenzione attiva sul ticket.
   */
  async stopTicketMaintenance(ticketId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}/maintenance/stop`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  /**
   * Estende la manutenzione attiva sul ticket (backend applica estensione predefinita).
   */
  async extendTicketMaintenance(ticketId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}/maintenance/extend`, {
      method: API_REQUEST_METHODS.POST
    });
    return !!response;
  }

  /**
   * Stato finestra manutenzione attiva per il ticket (GET).
   * 404 → nessuna finestra attiva (`undefined`); altri errori vengono loggati e rilanciati.
   */
  async getTicketMaintenanceActive(ticketId: string): Promise<FacilityMaintenanceView | undefined> {
    try {
      return await this.httpClient<FacilityMaintenanceView>(`tickets/${ticketId}/maintenance/active`, {
        method: API_REQUEST_METHODS.GET
      });
    } catch (error: unknown) {
      if (this.isMaintenanceActiveNotFoundError(error)) {
        return undefined;
      }
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async getTicketAttachments(ticketId: string): Promise<Attachment[]> {
    const response = await this.httpClient<Attachment[]>(`attachments/tickets/${ticketId}`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async deleteAttachment(attachmentId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`attachments/${attachmentId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response;
  }

  /**
   * Toglie una foto dalla risposta di una checklist. Decide lo stato della compilazione: in bozza chi
   * compila, su una completata o verificata chi la corregge (il Tecnico), su una condivisa nessuno.
   */
  async deleteChecklistAnswerAttachment(checklistAnswerId: string, attachmentId: string): Promise<boolean> {
    const response = await this.httpClient<boolean>(`attachments/checklist-answers/${checklistAnswerId}/${attachmentId}`, {
      method: API_REQUEST_METHODS.DELETE
    });

    return response;
  }

  async uploadAttachments(ticketId: string, formData: FormData): Promise<boolean> {
    const response = await this.httpClient<boolean>(`attachments/tickets/${ticketId}`, {
      method: API_REQUEST_METHODS.POST,
      body: formData,
      formData: true
    });

    return response;
  }

  async downloadAttachment(attachmentId: string): Promise<{ fileName: string, fileUrl: string }> {
    const response = await this.httpClient<{ fileName: string, fileUrl: string }>(`attachments/download/${attachmentId}`, {
      method: API_REQUEST_METHODS.GET,
      isFileDownload: true
    });

    return response;
  }

  async getTicketHistory(ticketId: string, fromRecord?: number, numRecords?: number): Promise<TicketHistory[]> {
    const response = await this.httpClient<History[]>(`tickets/${ticketId}/history`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord: fromRecord ?? 0,
        numRecords: numRecords ?? 10
      }
    });
    return response as TicketHistory[];
  }

  async getTicketHistoryCount(ticketId: string): Promise<number> {
    const response = await this.httpClient<{ count: number }>(`tickets/${ticketId}/history/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return response.count;
  }

  /**
   * Ottiene i contatori del dashboard con filtro area opzionale
   * @param areaId ID dell'area per filtrare i risultati (opzionale)
   * @returns Promise con i contatori del dashboard
   */
  async getDashboardCounters(areaId?: string | null): Promise<DashboardCounters> {
    const dashboardData = await this.httpClient<DashboardCounters>("dashboard/counters", {
      method: API_REQUEST_METHODS.GET,
      query: areaId ? { areaId } : undefined
    });

    return dashboardData;
  }

  /**
   * Ottiene la distribuzione dei ticket per stato con filtro area opzionale
   * @param areaId ID dell'area per filtrare i risultati (opzionale)
   * @returns Promise con la distribuzione per stato
   */
  async getDashboardStatusDistribution(areaId?: string | null): Promise<DashboardStatusDistribution> {
    const dashboardData = await this.httpClient<DashboardStatusDistribution>("dashboard/status-distribution", {
      method: API_REQUEST_METHODS.GET,
      query: areaId ? { areaId } : undefined
    });

    return dashboardData;
  }

  /**
   * Ottiene la distribuzione dei ticket per priorità con filtro area opzionale
   * @param areaId ID dell'area per filtrare i risultati (opzionale)
   * @returns Promise con la distribuzione per priorità
   */
  async getDashboardPriorityDistribution(areaId?: string | null): Promise<DashboardPriorityDistribution> {
    const dashboardData = await this.httpClient<DashboardPriorityDistribution>("dashboard/priority-distribution", {
      method: API_REQUEST_METHODS.GET,
      query: areaId ? { areaId } : undefined
    });

    return dashboardData;
  }

  /**
   * Ottiene i top impianti per numero di ticket con filtro area opzionale
   * @param areaId ID dell'area per filtrare i risultati (opzionale)
   * @returns Promise con i dati dei top impianti
   */
  async getDashboardTicketsByFacility(areaId?: string | null): Promise<DashboardTicketsByFacility[]> {
    const dashboardData = await this.httpClient<DashboardTicketsByFacility[]>("dashboard/tickets-by-facility", {
      method: API_REQUEST_METHODS.GET,
      query: areaId ? { areaId } : undefined
    });

    return dashboardData;
  }

  /**
   * Ottiene i dati mensili dei ticket per gli ultimi 2 anni con filtro area opzionale
   * @param areaId ID dell'area per filtrare i risultati (opzionale)
   * @returns Promise con i dati mensili dei ticket
   */
  async getDashboardTicketsLast2Years(areaId?: string | null): Promise<DashboardTicketsLast2Years[]> {
    const dashboardData = await this.httpClient<DashboardTicketsLast2Years[]>("dashboard/tickets-last-2-years", {
      method: API_REQUEST_METHODS.GET,
      query: areaId ? { areaId } : undefined
    });

    return dashboardData;
  }

  async downloadTicketsReport(ticketFiltersToApply: TicketFindParams): Promise<string> {
    const response = await this.httpClient<string>("tickets/excel-report", {
      method: API_REQUEST_METHODS.POST,
      body: ticketFiltersToApply
    });

    return response;
  }

  async getTicketStatusesToChange(ticketId: string): Promise<TicketStatusToChange> {
    const response = await this.httpClient<TicketStatusToChange>(`tickets/${ticketId}/possible-next-statuses`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  /**
   * Contratti di manutenzione disponibili per abbino al ticket in base all'impianto selezionato
   * @param facilityId - Identificativo impianto
   * @returns Elenco contratti (vista lista)
   */
  async getAvailableMaintenanceContractsForTicketFacility(id: string): Promise<MaintenanceContractListView[]> {
    if (!id?.trim()) {
      throw new Error("id è obbligatorio");
    }
    const response = await this.httpClient<MaintenanceContractListView[]>(
      `tickets/${id}/available-maintenance-contracts`,
      {
        method: API_REQUEST_METHODS.GET
      }
    );
    return response;
  }

  /**
   * Utenti a cui il ticket può essere assegnato.
   *
   * Sostituisce l'uso di `getUsers({ filter: { organization } })`: la sola organizzazione non basta,
   * perché lasciava in tendina utenti privi di operatività sui ticket (un Amministrativo non ne
   * vede nemmeno la sezione). Il backend incrocia organizzazione, ruolo e contesto.
   * @param ticketId - Identificativo del ticket da assegnare
   * @param organizationId - Organizzazione di cui cercare gli utenti; se omessa, quella del ticket
   * @returns Utenti assegnabili, ordinati per cognome e nome
   */
  async getTicketAssignableUsers(ticketId: string, organizationId?: string): Promise<User[]> {
    if (!ticketId?.trim()) {
      throw new Error("ticketId è obbligatorio");
    }
    const response = await this.httpClient<User[]>(
      `tickets/${ticketId}/assignable-users`,
      {
        method: API_REQUEST_METHODS.GET,
        query: {
          organizationId
        }
      }
    );
    return response;
  }

  /**
   * Aggiorna lo stato di un ticket
   * @param ticketId - Identificativo del ticket
   * @param status - Nuovo stato
   * @param closeOptions - Opzionale: per stato CLOSED indica se la chiusura è con riserva (coerente con contratto API)
   * @returns Esito dell'operazione
   */
  async changeTicketStatus(
    ticketId: string,
    status: TicketStatusEnum,
    closeOptions?: { closedWithWarning: boolean }
  ): Promise<boolean> {
    const body: { status: TicketStatusEnum; closedWithWarning?: boolean } = { status };
    if (closeOptions !== undefined) {
      body.closedWithWarning = closeOptions.closedWithWarning;
    }
    const response = await this.httpClient<boolean>(`tickets/${ticketId}/status`, {
      method: API_REQUEST_METHODS.POST,
      body
    });
    return response;
  }

  async transferTicket(ticketId: string, formData: { ticketId: string, assignee: { userId: string, name: string } | undefined, comment: string, isPrivate: boolean, facility: { facilityId: string, name: string } | undefined, organization: { organizationId: string, name: string } | undefined }): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${formData.ticketId}/assign`, {
      method: API_REQUEST_METHODS.POST,
      body: {
        assignedUserId: formData.assignee?.userId ?? undefined,
        comment: formData.comment,
        isPrivate: formData.isPrivate ?? false,
        facilityId: formData.facility?.facilityId,
        organizationId: formData.organization?.organizationId
      }
    });

    return response;
  }

  async assignTicketToSelf(ticketId: string, options?: { comment?: string; isPrivate?: boolean }): Promise<boolean> {
    const response = await this.httpClient<boolean>(`tickets/${ticketId}/assign`, {
      method: API_REQUEST_METHODS.POST,
      body: options ? {
        comment: options.comment,
        isPrivate: options.isPrivate
      } : {}
    });

    return response;
  }

  async getOrganizationHistoryDiff(organizationId: string, historyId: string): Promise<HistoryDiffInterface[]> {
    const response = await this.httpClient<HistoryDiffInterface[]>(`organizations/${organizationId}/history/${historyId}/diff`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async getFacilityHistoryDiff(facilityId: string, historyId: string): Promise<HistoryDiffInterface[]> {
    const response = await this.httpClient<HistoryDiffInterface[]>(`facilities/${facilityId}/history/${historyId}/diff`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async getTicketHistoryDiff(ticketId: string, historyId: string): Promise<HistoryDiffInterface[]> {
    const response = await this.httpClient<HistoryDiffInterface[]>(`tickets/${ticketId}/history/${historyId}/diff`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async subscribeToNotifications(userId: string, tenant: string, subscription: string, userAgent?: string): Promise<void> {
    await this.httpClient("push/subscribe", {
      method: API_REQUEST_METHODS.POST,
      body: {
        userId,
        tenant,
        subscription,
        deviceInfo: userAgent ? { userAgent } : undefined
      }
    });
  }

  async unsubscribeFromNotifications(endpoint: string): Promise<void> {
    await this.httpClient("push/unsubscribe", {
      method: API_REQUEST_METHODS.POST,
      body: {
        endpoint
      }
    });
  }

  /**
   * La radice degli endpoint del dominio manutenzione.
   *
   * Contratti e MANUTENZIONI INTERNE hanno controller distinti (`MaintenanceContractController` e
   * `InternalMaintenanceController`) che delegano agli stessi service: il secondo aggiunge il
   * controllo di contesto e la natura dell'entita'. Lato frontend le due pagine sono le stesse
   * componenti in due modalita', quindi i metodi sono gli stessi e cambia solo la radice.
   *
   * Il flag e' l'ultimo parametro, opzionale e con default "contratti": nessuna chiamata esistente
   * cambia comportamento.
   *
   * NOTA: sezioni, tariffario e rapportini NON hanno una radice interna. I loro endpoint sono
   * condivisi (`maintenance/sections/`, `maintenance/contracts/rate-cards/`,
   * `maintenance/contracts/expenses/`) e il backend li protegge sul dato, non sul path.
   */
  private maintenanceBase(internal?: boolean): string {
    return internal ? "maintenance/internal-maintenances" : "maintenance/contracts";
  }

  // maintenance contracts count
  async getMaintenanceContractsCount(params?: MaintenanceContractFindParams, internal?: boolean): Promise<number> {
    const queryParams = { ...params };
    const maintenanceContractsCount = await this.httpClient<number>(`${this.maintenanceBase(internal)}/count`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams
      }
    });
    return maintenanceContractsCount;
  }

  // maintenance contracts list
  async getMaintenanceContracts(params?: MaintenanceContractFindParams, sort?: { column: string, direction: "asc" | "desc" }, internal?: boolean): Promise<MaintenanceContractListView[]> {
    const queryParams = { ...params };
    if (!queryParams?.numRecords) {
      queryParams.numRecords = 10;
    }
    if (!queryParams?.fromRecord) {
      queryParams.fromRecord = 0;
    }

    const maintenanceContracts = await this.httpClient<MaintenanceContractListView[]>(`${this.maintenanceBase(internal)}/list`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        ...queryParams,
        sortBy: sort?.column,
        sortOrder: sort?.direction
      }
    });
    return maintenanceContracts;
  }

  /**
   * Ottiene le organizzazioni disponibili per i contratti di manutenzione
   * @returns Promise con le organizzazioni disponibili (id + nome per selezione)
   */
  async getMaintenanceContractOrganizations(searchedText?: string, internal?: boolean): Promise<MaintenanceContractOrganizationOption[]> {
    const organizations = await this.httpClient<MaintenanceContractOrganizationOption[]>(`${this.maintenanceBase(internal)}/available-organizations`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        searchedText
      }
    });
    return organizations;

    /* return new Promise((resolve) => {
      resolve([
      { organizationId: "1", name: "Fornitore 1" },
        { organizationId: "2", name: "Fornitore 2" },
        { organizationId: "3", name: "Fornitore 3" }
      ]);
    }); */
  }

  /**
   * Ottiene le aree disponibili per i contratti di manutenzione
   * @returns Promise con le aree disponibili (id + nome per selezione)
   */
  async getMaintenanceContractAreas(searchedText?: string, internal?: boolean): Promise<MaintenanceContractAreaOption[]> {
    const areas = await this.httpClient<MaintenanceContractAreaOption[]>(`${this.maintenanceBase(internal)}/available-areas`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        searchedText
      }
    });
    return areas;
    /* return new Promise((resolve) => {
      resolve([
        { areaId: "1", name: "Area 1" },
        { areaId: "2", name: "Area 2" },
        { areaId: "3", name: "Area 3" }
      ]);
    }); */
  }

  /**
   * Ottiene gli impianti disponibili per i contratti di manutenzione
   * @param areaIds ID delle aree per filtrare gli impianti
   * @returns Promise con gli impianti disponibili per le aree selezionate
   */
  async getMaintenanceContractFacilities(areaIds: string[], searchedText?: string, organizationIds?: string[], internal?: boolean): Promise<MaintenanceContractFacilityOption[]> {
    const facilities = await this.httpClient<MaintenanceContractFacilityOption[]>(`${this.maintenanceBase(internal)}/available-facilities`, {
      method: API_REQUEST_METHODS.GET,
      query: { areaIds, searchedText, ...(organizationIds?.length ? { organizationIds } : {}) }
    });
    return facilities;

    /* return new Promise((resolve) => {
      resolve([
        { facilityId: "1", name: "Impianto 1" },
        { facilityId: "2", name: "Impianto 2" },
        { facilityId: "3", name: "Impianto 3" }
      ]);
    }); */
  }

  /**
   * Ottiene i componenti disponibili per un impianto selezionato
   * @param facilityIds ID degli impianti per filtrare i componenti
   * @returns Promise con i componenti disponibili per l'impianto selezionato
   */
  /**
   * Impianti del contratto con relativi componenti (per creazione/modifica sezioni).
   * @param contractId - ID contratto di manutenzione
   */
  async getMaintenanceContractComponentsAndFacilities(contractId: string, internal?: boolean): Promise<MaintenanceContractFacilityAndFacilityComponent[]> {
    if (!contractId?.trim()) {
      throw new Error("contractId è obbligatorio");
    }
    const data = await this.httpClient<MaintenanceContractFacilityAndFacilityComponent[]>(
      `${this.maintenanceBase(internal)}/${encodeURIComponent(contractId.trim())}/components-and-facilities`,
      { method: API_REQUEST_METHODS.GET }
    );
    return Array.isArray(data) ? data : [];
  }

  async getMaintenanceContractFacilityComponents(facilityIds: string[], internal?: boolean): Promise<MaintenanceContractFacilityComponentOption[]> {
    const components = await this.httpClient<MaintenanceContractFacilityComponentOption[]>(`${this.maintenanceBase(internal)}/available-facility-components`, {
      method: API_REQUEST_METHODS.GET,
      query: { facilityIds }
    });
    return components;

    /* return new Promise((resolve) => {
      if (facilityIds.includes("1")) {
        resolve([
          { facilityComponentId: "1", facilityId: "1", name: "Componente 1" },
          { facilityComponentId: "11", facilityId: "1", name: "Componente 11" },
          { facilityComponentId: "111", facilityId: "1", name: "Componente 111" }
        ]);
      } else if (facilityIds.includes("2")) {
        resolve([
          { facilityComponentId: "2", facilityId: "2", name: "Componente 2" },
          { facilityComponentId: "22", facilityId: "2", name: "Componente 22" },
          { facilityComponentId: "222", facilityId: "2", name: "Componente 222" }
        ]);
      } else {
        resolve([]);
      }
    }); */
  }

  /**
   * Ottiene i codici dei contratti di manutenzione
   * @param searchedText Testo da cercare
   * @param fromRecord Offset di partenza
   * @param numRecords Numero di record da recuperare
   * @returns Promise con i codici dei contratti di manutenzione
   */
  async getMaintenanceContractsCodes(searchedText?: string, fromRecord?: number, numRecords?: number, internal?: boolean): Promise<{ data: string[], total: number }> {
    const response = await this.httpClient<{ data: string[], total: number }>(`${this.maintenanceBase(internal)}/filter/codes`, {
      method: API_REQUEST_METHODS.GET,
      query: { searchedText, fromRecord, numRecords }
    });
    return response;
  }

  /**
   * Ottiene i nomi dei contratti di manutenzione
   * @param searchedText Testo da cercare
   * @param fromRecord Offset di partenza
   * @param numRecords Numero di record da recuperare
   * @returns Promise con i nomi dei contratti di manutenzione
   */
  async getMaintenanceContractsNames(searchedText?: string, fromRecord?: number, numRecords?: number, internal?: boolean): Promise<{ data: string[], total: number }> {
    const response = await this.httpClient<{ data: string[], total: number }>(`${this.maintenanceBase(internal)}/filter/names`, {
      method: API_REQUEST_METHODS.GET,
      query: { searchedText, fromRecord, numRecords }
    });
    return response;
  }

  /**
   * Ottiene le organizzazioni disponibili per i contratti di manutenzione per i filtri
   * @param searchedText Testo da cercare
   * @param fromRecord Offset di partenza
   * @param numRecords Numero di record da recuperare
   * @returns Promise con le organizzazioni disponibili per i filtri
   */
  async getMaintenanceContractOrganizationsForFilters(searchedText?: string, fromRecord?: number, numRecords?: number, internal?: boolean): Promise<{ data: Organization[], total: number }> {
    // Restituisce anche il `total`, come le gemelle su codici e nomi: scartarlo obbligava il
    // chiamante a confrontare la lunghezza dell'elenco con se' stessa, quindi "ci sono altri
    // elementi" era sempre falso e il caricamento progressivo non partiva mai.
    const response = await this.httpClient<{ data: Organization[], total: number }>(`${this.maintenanceBase(internal)}/filter/organizations`, {
      method: API_REQUEST_METHODS.GET,
      query: { searchedText, fromRecord, numRecords }
    });
    return { data: response?.data ?? [], total: response?.total ?? 0 };
  }

  async deleteMaintenanceContract(contractId: string, internal?: boolean): Promise<boolean> {
    const response = await this.httpClient<boolean>(`${this.maintenanceBase(internal)}/${contractId}`, {
      method: API_REQUEST_METHODS.DELETE
    });
    return response;
  }

  /**
   * Cambia lo stato di un contratto di manutenzione
   * @param contractId - ID del contratto da aggiornare
   * @param status - Nuovo stato da impostare
   * @returns Promise con esito dell'operazione
   */
  async changeMaintenanceContractStatus(contractId: string, status: MaintenanceContractStatusEnum, internal?: boolean): Promise<boolean> {
    const response = await this.httpClient<boolean>(`${this.maintenanceBase(internal)}/${contractId}/status`, {
      method: API_REQUEST_METHODS.POST,
      body: { status }
    });
    return response;
  }

  /**
   * Copia i tariffari da un contratto sorgente al contratto corrente
   * @param sourceContractId - ID del contratto da cui copiare i tariffari
   * @param targetContractId - ID del contratto di destinazione
   * @returns Promise con esito dell'operazione
   */
  /**
   * Recupera i dettagli delle sezioni di un contratto di manutenzione.
   * @param contractId - ID del contratto
   * @returns Promise con l'elenco delle sezioni (MaintenanceSectionDetails)
   */
  async getSectionDetails(contractId: string, facilityIds?: string[], internal?: boolean): Promise<MaintenanceSectionDetails[]> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const response = await this.httpClient<MaintenanceSectionDetails[]>(`${this.maintenanceBase(internal)}/${contractId}/section-details`, {
      method: API_REQUEST_METHODS.GET,
      query: { facilityIds }
    });
    return response ?? [];
  }

  /**
   * Crea una sezione di manutenzione (POST maintenance/section/create).
   * @param body - Dati della sezione (MaintenanceSectionRequest)
   */
  async createSection(body: MaintenanceSectionRequest): Promise<boolean> {
    const response = await this.httpClient<boolean>("maintenance/sections/create", {
      method: API_REQUEST_METHODS.POST,
      body
    });
    return response;
  }

  /**
   * Aggiorna una sezione di manutenzione (PUT maintenance/sections/update/{sectionId}).
   * @param sectionId - ID della sezione
   * @param body - Dati della sezione (MaintenanceSectionRequest)
   */
  async updateSection(sectionId: string, body: MaintenanceSectionRequest): Promise<boolean> {
    if (!sectionId?.trim()) {
      throw new Error("ID sezione è richiesto");
    }
    const response = await this.httpClient<boolean>(`maintenance/sections/${sectionId}`, {
      method: API_REQUEST_METHODS.PUT,
      body
    });
    return response;
  }

  /**
   * Elimina una sezione di manutenzione (DELETE maintenance/sections/remove/{sectionId}).
   * @param sectionId - ID della sezione da rimuovere
   */
  async removeSection(sectionId: string): Promise<boolean> {
    if (!sectionId?.trim()) {
      throw new Error("ID sezione è richiesto");
    }
    const response = await this.httpClient<boolean>(`maintenance/sections/remove/${sectionId}`, {
      method: API_REQUEST_METHODS.DELETE
    });
    return response;
  }

  /**
   * Recupera i dettagli dei tariffari di un contratto di manutenzione.
   * @param contractId - ID del contratto
   * @returns Promise con l'elenco delle voci tariffario (ContractRateCard)
   */
  async getRateCards(contractId: string, internal?: boolean): Promise<ContractRateCard[]> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const response = await this.httpClient<ContractRateCard[]>(`${this.maintenanceBase(internal)}/${contractId}/rate-cards-details`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  /**
   * Elimina una voce tariffario.
   * @param rateCardId - ID della voce tariffario
   */
  async deleteRateCard(rateCardId: string): Promise<boolean> {
    if (!rateCardId?.trim()) {
      throw new Error("ID voce tariffario è richiesto");
    }
    const response = await this.httpClient<boolean>(`maintenance/contracts/rate-cards/${rateCardId}`, {
      method: API_REQUEST_METHODS.DELETE
    });
    return response;
  }

  /**
   * Aggiorna una voce tariffario.
   * @param rateCardId - ID della voce tariffario
   * @param body - Dati da aggiornare (itemType, laborType, activityType, mealType, transportType, unitCost, ecc.)
   */
  async updateRateCard(rateCardId: string, body: Partial<Omit<ContractRateCard, "rateCardId" | "contractId">>): Promise<boolean> {
    if (!rateCardId?.trim()) {
      throw new Error("ID voce tariffario è richiesto");
    }
    const response = await this.httpClient<boolean>(`maintenance/contracts/rate-cards/${rateCardId}`, {
      method: API_REQUEST_METHODS.PUT,
      body
    });
    return response;
  }

  async copyMaintenanceContractRateCards(sourceContractId: string, targetContractId: string): Promise<boolean> {
    if (!sourceContractId?.trim()) {
      throw new Error("ID contratto sorgente è richiesto");
    }
    if (!targetContractId?.trim()) {
      throw new Error("ID contratto destinazione è richiesto");
    }
    const response = await this.httpClient<boolean>(`maintenance/contracts/rate-cards/copy/${sourceContractId}/${targetContractId}`, {
      method: API_REQUEST_METHODS.POST
    });
    return response;
  }

  /**
   * Aggiorna contratto: stesso body della creazione ({@link MaintenanceContractFormRequest}).
   * @param contractId - ID contratto
   * @param body - Payload allineato al POST create
   */
  async updateMaintenanceContract(contractId: string, body: MaintenanceContractFormRequest, internal?: boolean): Promise<boolean> {
    const response = await this.httpClient<boolean>(`${this.maintenanceBase(internal)}/${contractId}`, {
      method: API_REQUEST_METHODS.PUT,
      body
    });
    return response;
  }

  /**
   * Elenco allegati di un contratto di manutenzione
   * @param contractId - ID contratto
   * @returns Lista allegati
   */
  async getMaintenanceContractAttachments(contractId: string, fromRecord: number, numRecords: number, internal?: boolean): Promise<Attachment[]> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<Attachment[]>(`${this.maintenanceBase(internal)}/${id}/attachments`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords
      }
    });
    return response ?? [];
  }

  async countMaintenanceContractAttachments(contractId: string, internal?: boolean): Promise<number> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<number>(`${this.maintenanceBase(internal)}/${id}/attachments/count`, {
      method: API_REQUEST_METHODS.GET
    });
    return response ?? 0;
  }

  /**
   * Carica allegati sul contratto (multipart, stesso pattern dei ticket)
   * @param contractId - ID contratto
   * @param formData - FormData con file1, file2, …
   * @returns Esito operazione
   */
  async uploadMaintenanceContractAttachments(contractId: string, formData: FormData, internal?: boolean): Promise<boolean> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<boolean>(`${this.maintenanceBase(internal)}/${id}/attachments`, {
      method: API_REQUEST_METHODS.POST,
      body: formData,
      formData: true
    });
    return response;
  }

  /**
   * Elimina un allegato del contratto
   * @param contractId - ID contratto
   * @param attachmentId - ID allegato
   */
  async deleteMaintenanceContractAttachment(contractId: string, attachmentId: string, internal?: boolean): Promise<boolean> {
    if (!contractId?.trim() || !attachmentId?.trim()) {
      throw new Error("ID contratto e ID allegato sono richiesti");
    }
    const cid = encodeURIComponent(contractId.trim());
    const aid = encodeURIComponent(attachmentId.trim());
    return this.httpClient<boolean>(`${this.maintenanceBase(internal)}/${cid}/attachments/${aid}`, {
      method: API_REQUEST_METHODS.DELETE
    });
  }

  /**
   * Download allegato contratto (URL firmato / blob come per i ticket)
   * @param contractId - ID contratto
   * @param attachmentId - ID allegato
   */
  async downloadMaintenanceContractAttachment(contractId: string, attachmentId: string, internal?: boolean): Promise<{ fileName: string; fileUrl: string }> {
    if (!contractId?.trim() || !attachmentId?.trim()) {
      throw new Error("ID contratto e ID allegato sono richiesti");
    }
    const cid = encodeURIComponent(contractId.trim());
    const aid = encodeURIComponent(attachmentId.trim());
    return this.httpClient<{ fileName: string; fileUrl: string }>(
      `${this.maintenanceBase(internal)}/${cid}/attachments/${aid}/download`,
      {
        method: API_REQUEST_METHODS.GET,
        isFileDownload: true
      }
    );
  }

  /**
   * Rapportini / consuntivi di costo per un contratto di manutenzione
   * @param contractId - ID contratto
   * @param fromRecord - Indice iniziale (0-based) per la paginazione lato server
   * @param numRecords - Numero di righe richieste
   * @returns Dettaglio con elenco rapportini (pagina) e totali aggregati
   */
  async getMaintenanceContractExpenseReports(
    contractId: string,
    fromRecord: number,
    numRecords: number
  ): Promise<MaintenanceContractExpenseReportsDetails> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<MaintenanceContractExpenseReportsDetails>(`maintenance/contracts/expenses/${id}`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        fromRecord,
        numRecords
      }
    });
    return response;
  }

  /**
   * Conteggio manutenzioni passate per contratto.
   * @param contractId - ID contratto
   * @param limitDate - Data limite in formato YYYY-MM-DD (inizio finestra “ultimi N mesi”: oggi meno N mesi)
   */
  async getContractPastMaintenancesCount(contractId: string, limitDate: string, internal?: boolean): Promise<number> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    if (!limitDate?.trim()) {
      throw new Error("limitDate è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<number>(`${this.maintenanceBase(internal)}/${id}/past-maintenances/count`, {
      method: API_REQUEST_METHODS.GET,
      query: { limitDate: limitDate.trim() }
    });
    return response;
  }

  async getContractPastMaintenancesList(
    contractId: string,
    limitDate: string,
    fromRecord: number,
    numRecords: number,
    internal?: boolean
  ): Promise<ContractMaintenanceView[]> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    if (!limitDate?.trim()) {
        throw new Error("limitDate è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<ContractMaintenanceView[]>(`${this.maintenanceBase(internal)}/${id}/past-maintenances/list`, {
      method: API_REQUEST_METHODS.GET,
      query: { limitDate: limitDate.trim(), fromRecord, numRecords }
    });
    return response;
  }

  /**
   * Conteggio prossime manutenzioni/scadenze per contratto.
   * @param contractId - ID contratto
   * @param limitDate - Data limite `YYYY-MM-DD` (es. finestra fino a oggi + N mesi dal dettaglio contratto)
   */
  async getContractNextMaintenancesCount(
    contractId: string,
    limitDate: string,
    internal?: boolean
  ): Promise<number> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    if (!limitDate?.trim()) {
      throw new Error("limitDate è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<number>(`${this.maintenanceBase(internal)}/${id}/next-maintenances/count`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        limitDate: limitDate.trim()
      }
    });
    
    return response;
  }

  /**
   * Pagina elenco prossime manutenzioni per contratto.
   * @param contractId - ID contratto
   * @param limitDate - Data limite in formato YYYY-MM-DD (coerente con {@link getContractNextMaintenancesCount})
   * @param fromRecord - Offset 0-based
   * @param numRecords - Dimensione pagina
   */
  async getContractNextMaintenancesList(
    contractId: string,
    limitDate: string,
    fromRecord: number,
    numRecords: number,
    internal?: boolean
  ): Promise<ContractMaintenanceView[]> {
    if (!contractId?.trim()) {
      throw new Error("ID contratto è richiesto");
    }
    if (!limitDate?.trim()) {
      throw new Error("limitDate è richiesto");
    }
    const id = encodeURIComponent(contractId.trim());
    const response = await this.httpClient<ContractMaintenanceView[]>(`${this.maintenanceBase(internal)}/${id}/next-maintenances/list`, {
      method: API_REQUEST_METHODS.GET,
      query: {
        limitDate: limitDate.trim(),
        fromRecord,
        numRecords
      }
    });
    
    return response;
  }

  async getMaintenanceContract(contractId: string, internal?: boolean): Promise<MaintenanceContractDetails> {
    const response = await this.httpClient<MaintenanceContractDetails>(`${this.maintenanceBase(internal)}/${contractId}/details`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async createMaintenanceContract(body: MaintenanceContractFormRequest, internal?: boolean): Promise<MaintenanceContract> {
    const response = await this.httpClient<MaintenanceContract>(`${this.maintenanceBase(internal)}/create`, {
      method: API_REQUEST_METHODS.POST,
      body
    });
    return response;
  }

  async insertRateCard(contractId: string, itemType: RateCardItemTypeEnum, laborType: LaborTypeEnum, activityType: ActivityTypeEnum, mealType: MealTypeEnum, transportType: TransportTypeEnum, rentalType: TransportRentalTypeEnum, unitCost: number): Promise<boolean> {
    const response = await this.httpClient<boolean>(`maintenance/contracts/rate-cards/create/${contractId}`, {
      method: API_REQUEST_METHODS.POST,
      body: { itemType, laborType, activityType, mealType, transportType, rentalType, unitCost }
    });
    return response;
  }

  async getMaintenanceContractForTicket(ticketId: string): Promise<MaintenanceContractListView> {
    const response = await this.httpClient<MaintenanceContractListView>(`tickets/${ticketId}/maintenance-contract`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  async getMaintenanceContractExpenseReportForTicket(ticketId: string): Promise<ExpenseReportDetails> {
    return this.getTicketExpenseReport(ticketId);
  }

  /**
   * Rapportino di costo del ticket (`GET tickets/{ticketId}/expense-report`)
   * @param ticketId - ID ticket
   * @returns Dettaglio rapportino con voci
   */
  async getTicketExpenseReport(ticketId: string): Promise<ExpenseReportDetails> {
    if (!ticketId?.trim()) {
      throw new Error("ID ticket è richiesto");
    }
    const id = encodeURIComponent(ticketId.trim());
    const response = await this.httpClient<ExpenseReportDetails>(`tickets/${id}/expense-report`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  /**
   * Elenco rate cards di un ticket
   * @param ticketId - ID ticket
   * @returns Lista rate cards
   */
  async getTicketRateCards(ticketId: string): Promise<ContractRateCard[]> {
    const response = await this.httpClient<ContractRateCard[]>(`tickets/${ticketId}/rate-cards`, {
      method: API_REQUEST_METHODS.GET
    });
    return response;
  }

  /**
   * Aggiunge nuove voci al rapportino (senza `expenseItemId` / `totalCost` in ingresso).
   * @param expenseReportId - ID rapportino
   * @param items - Voci da creare
   */
  async postMaintenanceContractExpenseReportAddItems(
    expenseReportId: string,
    items: ExpenseReportAddItemsEntry[]
  ): Promise<boolean> {
    if (!expenseReportId?.trim()) {
      throw new Error("ID rapportino è richiesto");
    }
    const id = encodeURIComponent(expenseReportId.trim());
    const response = await this.httpClient<boolean>(`maintenance/contracts/expenses/add-items/${id}`, {
      method: API_REQUEST_METHODS.POST,
      body: { items }
    });
    return response;
  }

  /**
   * Aggiorna voci già persistite del rapportino.
   * @param expenseReportId - ID rapportino
   * @param items - Voci con `expenseItemId`
   */
  async postMaintenanceContractExpenseReportUpdateItems(
    expenseReportId: string,
    items: ExpenseReportItem[]
  ): Promise<boolean> {
    if (!expenseReportId?.trim()) {
      throw new Error("ID rapportino è richiesto");
    }
    const id = encodeURIComponent(expenseReportId.trim());
    const response = await this.httpClient<boolean>(`maintenance/contracts/expenses/update-items/${id}`, {
      method: API_REQUEST_METHODS.POST,
      body: { items }
    });
    return response;
  }

  /**
   * Elimina voci del rapportino per ID voce.
   * @param expenseReportId - ID rapportino
   * @param expenseItemIds - ID voci da eliminare
   */
  async postMaintenanceContractExpenseReportDeleteItems(
    expenseReportId: string,
    expenseItemIds: string[]
  ): Promise<boolean> {
    if (!expenseReportId?.trim()) {
      throw new Error("ID rapportino è richiesto");
    }
    const id = encodeURIComponent(expenseReportId.trim());
    const response = await this.httpClient<boolean>(`maintenance/contracts/expenses/delete-items/${id}`, {
      method: API_REQUEST_METHODS.POST,
      body: { expenseItemIds }
    });
    return response;
  }

  /**
   * Aggiorna intestazione rapportino (note e imputazione cliente).
   * @param expenseReportId - ID rapportino
   * @param body - Note e flag `chargeableToClient`
   */
  async putMaintenanceContractExpenseReport(
    expenseReportId: string,
    body: { notes: string; chargeableToClient: boolean }
  ): Promise<boolean> {
    if (!expenseReportId?.trim()) {
      throw new Error("ID rapportino è richiesto");
    }
    const id = encodeURIComponent(expenseReportId.trim());
    const response = await this.httpClient<boolean>(`maintenance/contracts/expenses/${id}`, {
      method: API_REQUEST_METHODS.PUT,
      body
    });
    return response;
  }

  async downloadFacilityPDFReport(facilityId: string, startingFrom?: string, endingAt?: string): Promise<{ fileName: string; fileUrl: string }> {
    if (!facilityId?.trim()) {
      throw new Error("Facility ID is required");
    }
    if (!startingFrom?.trim() || !endingAt?.trim()) {
      throw new Error("Dates not valid");
    }
    const id = encodeURIComponent(facilityId.trim());
    return this.httpClient<{ fileName: string; fileUrl: string }>(`facilities/${id}/pdf-report`, {
      method: API_REQUEST_METHODS.GET,
      isFileDownload: true,
      query: {
        startingFrom,
        endingAt
      }
    });
  }

  /**
   * Scarica il libretto Excel dell'impianto per l'intervallo di date indicato
   * @param facilityId - Identificativo impianto
   * @param startingFrom - Data inizio (YYYY-MM-DD)
   * @param endingAt - Data fine (YYYY-MM-DD)
   * @returns Nome file e URL per il download
   */
  async downloadFacilityExcelReport(facilityId: string, startingFrom?: string, endingAt?: string): Promise<{ fileName: string; fileUrl: string }> {
    if (!facilityId?.trim()) {
      throw new Error("Facility ID is required");
    }
    if (!startingFrom?.trim() || !endingAt?.trim()) {
      throw new Error("Dates not valid");
    }
    const id = encodeURIComponent(facilityId.trim());
    return this.httpClient<{ fileName: string; fileUrl: string }>(`facilities/${id}/excel-report`, {
      method: API_REQUEST_METHODS.GET,
      isFileDownload: true,
      query: {
        startingFrom,
        endingAt
      }
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Checklist di impianto                                                     */
  /*                                                                           */
  /* Le revisioni sono silenziose: non ci sono endpoint per crearle o           */
  /* pubblicarle, tutta la scrittura del modello passa dal solo PUT.            */
  /* ------------------------------------------------------------------------ */

  /**
   * Elenco delle checklist di un impianto, con revisione corrente e conteggi.
   *
   * Le checklist STRAORDINARIE sono sempre escluse, tranne quella del ticket indicato:
   * sono usa-e-getta di un singolo ticket, non modelli dell'impianto. Chi mostra le
   * checklist di un impianto (dettaglio impianto, selettore di sezione) NON deve passare
   * il ticketId; la sezione Checklist del dettaglio ticket sì, o la straordinaria appena
   * creata non comparirebbe da nessuna parte.
   *
   * Col ticketId l'elenco non è più quello dell'impianto ma quello del TICKET, e il backend lo
   * restringe di conseguenza: se il ticket è generato da una sezione di contratto valgono solo
   * le checklist scelte da quella sezione, se è manuale valgono tutte quelle dell'impianto.
   * @param facilityId - ID dell'impianto
   * @param ticketId - Ticket di cui elencare le checklist, straordinaria compresa
   */
  async getChecklistsByFacility(facilityId: string, ticketId?: string): Promise<ChecklistListView[]> {
    if (!facilityId?.trim()) {
      throw new Error("ID impianto è richiesto");
    }
    const trimmedTicketId = ticketId?.trim();
    const response = await this.httpClient<ChecklistListView[]>("checklists/list", {
      method: API_REQUEST_METHODS.GET,
      query: { facilityId: [facilityId.trim()], ticketId: trimmedTicketId ? trimmedTicketId : undefined }
    });
    return response ?? [];
  }

  /**
   * La checklist STRAORDINARIA di un ticket, creandola se non c'è ancora.
   *
   * È la voce "checklist completa dell'impianto" del selettore nella sezione Checklist del
   * ticket: l'unione di tutte le checklist dell'impianto, con tutto opzionale e senza
   * periodicità, per chi non sa quale scegliere. La chiamata è IDEMPOTENTE — se il ticket ha
   * già la sua straordinaria restituisce quella — quindi un doppio clic non genera due
   * checklist.
   * @param ticketId - Ticket per cui serve la checklist completa dell'impianto
   */
  async createExtraordinaryChecklistForTicket(ticketId: string): Promise<Checklist> {
    if (!ticketId?.trim()) {
      throw new Error("ID ticket è richiesto");
    }
    return this.httpClient<Checklist>("checklists/extraordinary", {
      method: API_REQUEST_METHODS.POST,
      query: { ticketId: ticketId.trim() }
    });
  }

  /**
   * Quante checklist ha un impianto.
   *
   * Serve alla spunta "Crea anche checklist straordinaria" in creazione ticket, che ha
   * senso solo se c'è qualcosa da unire: si conta invece di scaricare l'elenco, perché
   * l'unica informazione che serve è "almeno una".
   * @param facilityId - ID dell'impianto
   */
  async countChecklistsByFacility(facilityId: string): Promise<number> {
    if (!facilityId?.trim()) {
      return 0;
    }
    const response = await this.httpClient<number>("checklists/count", {
      method: API_REQUEST_METHODS.GET,
      query: { facilityId: [facilityId.trim()] }
    });
    return response ?? 0;
  }

  /**
   * Contenuto completo di una checklist.
   * @param checklistId - ID della checklist
   * @param revisionId - Revisione da leggere; se assente si usa quella corrente
   */
  async getChecklistDetails(checklistId: string, revisionId?: string): Promise<ChecklistDetails> {
    if (!checklistId?.trim()) {
      throw new Error("ID checklist è richiesto");
    }
    const id = encodeURIComponent(checklistId.trim());
    return this.httpClient<ChecklistDetails>(`checklists/${id}`, {
      method: API_REQUEST_METHODS.GET,
      query: { revisionId }
    });
  }

  /**
   * Crea una checklist con la sua prima revisione e i suoi elementi.
   * @param body - Contenuto completo della checklist
   */
  async createChecklist(body: ChecklistRequest): Promise<ChecklistDetails> {
    return this.httpClient<ChecklistDetails>("checklists/create", {
      method: API_REQUEST_METHODS.POST,
      body
    });
  }

  /**
   * Sostituisce integralmente il contenuto della checklist.
   *
   * Se la revisione corrente ha già una compilazione il backend ne fa nascere una
   * nuova: gli identificativi degli elementi nella risposta sono in quel caso
   * diversi da quelli inviati, e vanno ricaricati da qui.
   * @param checklistId - ID della checklist
   * @param body - Contenuto completo che sostituisce quello esistente
   */
  async updateChecklist(checklistId: string, body: ChecklistRequest): Promise<ChecklistDetails> {
    if (!checklistId?.trim()) {
      throw new Error("ID checklist è richiesto");
    }
    const id = encodeURIComponent(checklistId.trim());
    return this.httpClient<ChecklistDetails>(`checklists/${id}`, {
      method: API_REQUEST_METHODS.PUT,
      body
    });
  }

  /**
   * Elimina una checklist. Due esiti, decisi dal backend sul dato:
   * - senza compilazioni, eliminazione logica con revisioni, elementi e opzioni;
   * - con compilazioni, l'eliminazione vera non è possibile e la chiamata viene rifiutata con
   *   `CHECKLIST_CANNOT_DELETE_HAS_SUBMISSIONS`. Ripetendola con `confirm = true` la checklist
   *   viene ARCHIVIATA: sparisce dagli elenchi e non è più compilabile, ma le compilazioni
   *   esistenti restano consultabili e quelle in corso completabili.
   *
   * È volutamente lo stesso endpoint: l'azione richiesta è sempre "elimina", e `confirm` dice
   * soltanto che l'utente ha già visto l'avviso sulle compilazioni collegate.
   *
   * @param checklistId - ID della checklist
   * @param confirm - l'utente ha confermato l'archiviazione
   */
  async deleteChecklist(checklistId: string, confirm = false): Promise<boolean> {
    if (!checklistId?.trim()) {
      throw new Error("ID checklist è richiesto");
    }
    const id = encodeURIComponent(checklistId.trim());
    const query = confirm ? "?confirm=true" : "";
    const response = await this.httpClient<boolean>(`checklists/${id}${query}`, {
      method: API_REQUEST_METHODS.DELETE
    });
    return response;
  }

  /**
   * Elenco delle revisioni di una checklist, dalla più recente.
   * Serve a ispezionare la storia del modello: non c'è alcuna azione da compiere
   * su una revisione.
   * @param checklistId - ID della checklist
   */
  async getChecklistRevisions(checklistId: string): Promise<ChecklistRevision[]> {
    if (!checklistId?.trim()) {
      throw new Error("ID checklist è richiesto");
    }
    const id = encodeURIComponent(checklistId.trim());
    const response = await this.httpClient<ChecklistRevision[]>(`checklists/${id}/revisions`, {
      method: API_REQUEST_METHODS.GET
    });
    return response ?? [];
  }

  /**
   * Categorie già usate a sistema che iniziano col testo indicato, in ordine alfabetico.
   *
   * Alimenta l'autocomplete del nome categoria: la ricerca è per PREFISSO e insensibile al caso
   * ("Elett" → "Elettrico", "Elettromeccanico"). Testo vuoto = le prime categorie esistenti.
   * La categoria resta un attributo testuale libero: questo elenco suggerisce, non vincola.
   * @param searchedText - Testo iniziale della categoria
   * @param fromRecord - Indice del primo record
   * @param numRecords - Quanti record restituire
   */
  async getChecklistCategories(searchedText?: string, fromRecord?: number, numRecords?: number): Promise<{ data: string[], total: number }> {
    const response = await this.httpClient<{ data: string[], total: number }>("checklists/categories", {
      method: API_REQUEST_METHODS.GET,
      query: {
        searchedText,
        fromRecord,
        numRecords
      }
    });
    return response ?? { data: [], total: 0 };
  }

  /* ------------------------------------------------------------------------ */
  /* Compilazioni di checklist                                                 */
  /*                                                                           */
  /* Una compilazione è sempre legata a un ticket. Gli stati sono              */
  /* DRAFT -> COMPLETED -> PUBLISHED, in avanti e senza ritorno: dopo la       */
  /* conferma la compilazione è immutabile, quindi non esiste alcun metodo di  */
  /* modifica oltre a `saveChecklistAnswers`, che agisce solo sulle bozze.     */
  /* ------------------------------------------------------------------------ */

  /**
   * Elenco delle compilazioni visibili all'utente.
   *
   * Le compilazioni CONDIVISE non passano da qui: hanno endpoint propri
   * (`getSharedChecklistSubmissions`), perché richiedono un altro permesso.
   * @param filters - Filtri di ricerca
   */
  /**
   * I componenti indicabili come causa di una anomalia sul ticket indicato.
   *
   * Non è l'elenco dei componenti dell'impianto: quando il ticket porta un contratto — abbinato a
   * mano oppure ereditato dalla schedulazione di una sezione — il backend restituisce i soli
   * componenti del perimetro del contratto.
   */
  async getChecklistAnomalyTargetComponents(ticketId: string): Promise<FacilityComponent[]> {
    const response = await this.httpClient<FacilityComponent[]>("checklists/submissions/target-components", {
      method: API_REQUEST_METHODS.GET,
      query: { ticketId }
    });
    return response ?? [];
  }

  async getChecklistSubmissions(filters: {
    ticketId?: string;
    facilityId?: string[];
    checklistId?: string;
    maintenanceContractId?: string;
    status?: ChecklistSubmissionStatusEnum[];
    fromRecord?: number;
    numRecords?: number;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<ChecklistSubmissionListView[]> {
    const response = await this.httpClient<ChecklistSubmissionListView[]>("checklists/submissions/list", {
      method: API_REQUEST_METHODS.GET,
      query: { ...filters }
    });
    return response ?? [];
  }

  /**
   * Numero di compilazioni visibili con i filtri indicati.
   * @param filters - Stessi filtri di `getChecklistSubmissions`, senza paginazione
   */
  async countChecklistSubmissions(filters: {
    ticketId?: string;
    facilityId?: string[];
    checklistId?: string;
    maintenanceContractId?: string;
    status?: ChecklistSubmissionStatusEnum[];
  }): Promise<number> {
    const response = await this.httpClient<number>("checklists/submissions/count", {
      method: API_REQUEST_METHODS.GET,
      query: { ...filters }
    });
    return response ?? 0;
  }

  /**
   * Contenuto completo di una compilazione: le voci già filtrate per periodicità e le
   * risposte date finora.
   * @param submissionId - ID della compilazione
   */
  async getChecklistSubmissionDetails(submissionId: string): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/${id}`, {
      method: API_REQUEST_METHODS.GET
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Compilazioni CONDIVISE                                                    */
  /*                                                                           */
  /* Endpoint separati e non un filtro dei precedenti, perché il permesso è     */
  /* un altro: VIEW_SHARED_CHECKLISTS la hanno tutti i profili, mentre gli      */
  /* endpoint generici chiedono READ_CHECKLIST_LIST / READ_CHECKLIST. La        */
  /* pagina spetta a chi è referente di un'area, e referente può diventarlo     */
  /* qualunque utente, anche uno che non legge le checklist.                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Le compilazioni condivise con l'utente: quelle pubblicate degli impianti delle aree di cui
   * è referente. Il perimetro è deciso dal backend e non è filtrabile dal client.
   * @param filters - Solo paginazione e ordinamento
   */
  async getSharedChecklistSubmissions(filters: {
    fromRecord?: number;
    numRecords?: number;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<ChecklistSubmissionListView[]> {
    const response = await this.httpClient<ChecklistSubmissionListView[]>("checklists/submissions/shared/list", {
      method: API_REQUEST_METHODS.GET,
      query: { ...filters }
    });
    return response ?? [];
  }

  /** Numero delle compilazioni condivise con l'utente, per la paginazione della pagina. */
  async countSharedChecklistSubmissions(): Promise<number> {
    const response = await this.httpClient<number>("checklists/submissions/shared/count", {
      method: API_REQUEST_METHODS.GET
    });
    return response ?? 0;
  }

  /**
   * Contenuto completo di una compilazione CONDIVISA con l'utente.
   *
   * Stesso payload di `getChecklistSubmissionDetails`, ma sull'endpoint dei condivisi: è la
   * strada di chi apre la scheda dalla pagina "Checklist condivise", che può non avere
   * `READ_CHECKLIST`.
   * @param submissionId - ID della compilazione
   */
  async getSharedChecklistSubmissionDetails(submissionId: string): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/shared/${id}`, {
      method: API_REQUEST_METHODS.GET
    });
  }

  /**
   * Anteprima del modulo di compilazione, senza creare la compilazione.
   *
   * È con questa che il pannello si apre: la compilazione nasce al primo salvataggio in bozza
   * o alla conferma. Nella risposta `submission` è assente e `answers` è vuoto.
   * @param ticketId - Ticket a cui la compilazione si legherà
   * @param checklistId - Checklist da compilare
   */
  async previewChecklistSubmission(ticketId: string, checklistId: string): Promise<ChecklistSubmissionDetails> {
    if (!ticketId?.trim() || !checklistId?.trim()) {
      throw new Error("ID ticket e ID checklist sono richiesti");
    }
    return this.httpClient<ChecklistSubmissionDetails>("checklists/submissions/preview", {
      method: API_REQUEST_METHODS.GET,
      query: { ticketId: ticketId.trim(), checklistId: checklistId.trim() }
    });
  }

  /**
   * Crea una compilazione per una checklist su un ticket.
   *
   * È qui che il server congela la periodicità del controllo: da questo momento l'insieme
   * di voci mostrate non cambia più, anche se la periodicità della sezione di contratto
   * viene modificata.
   * @param ticketId - Ticket a cui la compilazione si lega
   * @param checklistId - Checklist da compilare
   */
  async createChecklistSubmission(ticketId: string, checklistId: string): Promise<ChecklistSubmissionDetails> {
    if (!ticketId?.trim() || !checklistId?.trim()) {
      throw new Error("ID ticket e ID checklist sono richiesti");
    }
    return this.httpClient<ChecklistSubmissionDetails>("checklists/submissions/create", {
      method: API_REQUEST_METHODS.POST,
      query: { ticketId: ticketId.trim(), checklistId: checklistId.trim() }
    });
  }

  /**
   * Salva le risposte di una compilazione in bozza.
   *
   * La risposta contiene gli esiti di conformità calcolati dal server e i contatori
   * aggiornati: è l'unica fonte attendibile, il client non li calcola.
   * @param submissionId - ID della compilazione
   * @param answers - Risposte da salvare
   */
  async saveChecklistAnswers(submissionId: string, answers: ChecklistAnswerRequest[]): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/${id}/answers`, {
      method: API_REQUEST_METHODS.POST,
      body: { answers } as ChecklistSubmissionRequest
    });
  }

  /**
   * Completa una compilazione, rendendola immutabile.
   * @param submissionId - ID della compilazione
   */
  async completeChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/${id}/complete`, {
      method: API_REQUEST_METHODS.POST
    });
  }

  /**
   * Marca come verificata dal tecnico una compilazione completata (COMPLETED -> VERIFIED).
   *
   * È uno stato facoltativo: la condivisione col cliente resta possibile anche senza. Il backend
   * rifiuta con `CHECKLIST_SUBMISSION_NOT_VERIFIABLE` ogni stato diverso da COMPLETED.
   * @param submissionId - ID della compilazione
   */
  async verifyChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/${id}/verify`, {
      method: API_REQUEST_METHODS.POST
    });
  }

  /**
   * Condivide col cliente una compilazione completata o verificata.
   * @param submissionId - ID della compilazione
   */
  async publishChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetails> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    return this.httpClient<ChecklistSubmissionDetails>(`checklists/submissions/${id}/publish`, {
      method: API_REQUEST_METHODS.POST
    });
  }

  /**
   * Elimina una compilazione in bozza.
   * @param submissionId - ID della compilazione
   */
  async deleteChecklistSubmission(submissionId: string): Promise<boolean> {
    if (!submissionId?.trim()) {
      throw new Error("ID compilazione è richiesto");
    }
    const id = encodeURIComponent(submissionId.trim());
    const response = await this.httpClient<boolean>(`checklists/submissions/${id}`, {
      method: API_REQUEST_METHODS.DELETE
    });
    return response ?? false;
  }

  /**
   * Allega immagini a una risposta di checklist.
   *
   * Il backend espone `file1..file5`, quindi si caricano al massimo cinque immagini per
   * chiamata: il chiamante spezza i lotti più grandi.
   * @param checklistAnswerId - ID della risposta a cui allegare le immagini
   * @param files - Immagini da caricare, al massimo cinque
   */
  async addChecklistAnswerAttachments(checklistAnswerId: string, files: File[]): Promise<boolean> {
    if (!checklistAnswerId?.trim()) {
      throw new Error("ID risposta è richiesto");
    }
    if (!files?.length) {
      return true;
    }
    const id = encodeURIComponent(checklistAnswerId.trim());
    const formData = new FormData();
    files.slice(0, 5).forEach((file, index) => {
      formData.append(`file${index + 1}`, file, file.name);
    });
    const response = await this.httpClient<boolean>(`attachments/checklist-answers/${id}`, {
      method: API_REQUEST_METHODS.POST,
      body: formData,
      formData: true
    });
    return response ?? false;
  }

  /**
   * Organizzazioni di cui l'utente corrente è il responsabile.
   * È il predicato che mostra o nasconde la pagina delle checklist condivise: non dipende
   * dal ruolo ma dall'essere stati impostati come responsabile.
   */
  async getOrganizationsManagedByMe(): Promise<Organization[]> {
    const response = await this.httpClient<Organization[]>("organizations/managed-by-me", {
      method: API_REQUEST_METHODS.GET
    });
    return response ?? [];
  }
}

export interface CreateUploadFromFileRequest {
  file: FileData;
  uploadType: UploadTypeEnum;
}

export interface FileData {
  originalname: string;
  mimetype: string;
  size: number;
  buffer?: Buffer;
}

export type TicketStatusToChange = {
  currentStatus: TicketStatusEnum;
  possibleStatuses: TicketStatusEnum[];
};

/** Opzione organizzazione per selezione nei contratti di manutenzione (risposta API available-organizations) */
export interface MaintenanceContractOrganizationOption {
  organizationId: string;
  name: string;
}

/** Opzione area per selezione nei contratti di manutenzione (risposta API available-areas) */
export interface MaintenanceContractAreaOption {
  areaId: string;
  name: string;
}

/** Opzione impianto per selezione nei contratti di manutenzione (risposta API available-facilities) */
export interface MaintenanceContractFacilityOption {
  facilityId: string;
  name: string;
  /** Numero di contratti in cui l'impianto è già presente */
  alreadyInContractCount?: number;
}

/** Opzione componente per selezione nei contratti di manutenzione (risposta API available-facility-components) */
export interface MaintenanceContractFacilityComponentOption {
  facilityComponentId: string;
  facilityId: string;
  name: string;
  /** Numero di contratti in cui il componente è già presente */
  alreadyInContractCount?: number;
}